<template>
    <div class="flex-1 bg-1 relative flex flex-col">
        <!-- Progress Bar -->
        <div v-if="!submitted && !error" class="bg-2 fixed w-full z-30">
            <div class="progress-bar-container">
                <div class="progress-bar-fill" :style="{ width: progressPercentage + '%' }">
                    <span class="progress-bar-text">{{ Math.round(progressPercentage) }}%</span>
                </div>
            </div>
        </div>
        <div class="bg-3 ht-300 absolute w-full hidden sm:block"></div>
        <div class="z-20 mt-4 sm:mt-12 pb-0 sm:pb-20">
            <div class="container lg:max-w-4xl mx-auto">
                <!-- Error State -->
                <div v-if="error" class="bg-white rounded-xl shadow-lg p-8 sm:p-12 text-center max-w-2xl mx-auto">
                    <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
                        <font-awesome-icon icon="exclamation-circle" class="text-5xl text-red-500" />
                    </div>
                    <h2 class="text-3xl font-bold text-gray-900 mb-3">Survey Not Available</h2>
                    <p class="text-gray-600 text-lg leading-relaxed">{{ error }}</p>
                </div>

                <!-- Survey Form -->
                <div v-else-if="survey" class="bg-white box-shadow-2 sm:rounded-3xl px-6 sm:px-8 py-6 sm:py-8 relative">
                    <!-- Survey Header Card -->
                    <div class="flex flex-col text-color-1">
                        <div v-if="surveyThumbnail" class="mb-8">
                            <img :src="surveyThumbnail" alt="Survey Thumbnail" class="w-full h-full object-cover">
                        </div>
                        <h1 v-if="shouldShowTitle" class="text-2xl sm:text-3xl font-bold mb-2 leading-tight">{{
                            survey.title
                        }}</h1>
                        <div v-if="survey.description" class="text-sm sm:text-base leading-relaxed survey-description"
                            v-html="survey.description"></div>
                    </div>

                    <!-- Survey Form -->
                    <form @submit.prevent="submitResponse" v-if="!submitted" class="mt-8">
                        <!-- Hidden file input for file upload questions (shared across all file upload questions) -->
                        <input ref="fileUploadInput" type="file" class="hidden" @change="onFileUploadChange($event)" />
                        
                        <div class="">
                            <div v-if="hasPagedSurveyFlow && totalPages > 1" class="survey-page-indicator">
                                <span class="survey-page-indicator-label">Page {{ currentPageIndex + 1 }} of {{ totalPages }}</span>
                            </div>

                            <div v-if="currentPageTitle" class="survey-page-title-wrap" :style="currentPageTitleBorderStyle">
                                <h2 class="survey-page-title" :style="{ color: currentPageTitleColor }">{{ currentPageTitle }}</h2>
                            </div>

                            <div class="space-y-8">
                                <div v-for="(question, index) in questionsToDisplay" :key="question.id" class="pb-8">
                                    <!-- Section Header -->
                                    <div v-if="question.type === 'section_header'"
                                        class="pt-6 mt-6 first:pt-0 first:mt-0"
                                        :style="sectionHeaderBorderStyleForQuestion(question)">
                                        <h3 class="text-xl font-bold"
                                            :style="{ color: getSectionTitleColor(question) }">
                                            {{ question.question_text || 'Section' }}
                                        </h3>
                                        <p class="text-sm text-indigo-600/80 mt-1">The following questions belong to this section.</p>
                                    </div>

                                    <!-- Question Block (non-section_header) -->
                                    <template v-else>
                                    <!-- Question Header -->
                                    <div class="mb-2">
                                        <label class="flex items-center space-x-2">
                                            <span
                                                class="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-700 font-bold text-sm mt-0.5">
                                                {{ questionNumberForDisplay(index) }}
                                            </span>
                                            <span class="flex-1 text-lg font-semibold text-gray-900 leading-relaxed">
                                                {{ question.question_text }}
                                                <span v-if="question.is_required" class="text-red-500 ml-1">*</span>
                                            </span>
                                        </label>
                                    </div>

                                    <!-- Text Input -->
                                    <div v-if="question.type === 'text'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="text"
                                            :required="question.is_required" placeholder="Type your answer here..."
                                            class="survey-input" />
                                    </div>

                                    <!-- Textarea -->
                                    <div v-else-if="question.type === 'textarea'" class="mt-4">
                                        <textarea v-model="answers[question.id].answer_text" rows="5"
                                            :required="question.is_required"
                                            placeholder="Type your detailed answer here..."
                                            class="survey-textarea"></textarea>
                                    </div>

                                    <!-- Number -->
                                    <div v-else-if="question.type === 'number'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="number"
                                            :required="question.is_required" placeholder="Enter a number..."
                                            class="survey-input" />
                                    </div>

                                    <!-- Website -->
                                    <div v-else-if="question.type === 'website'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="url"
                                            :required="question.is_required" placeholder="Enter website URL..."
                                            class="survey-input" />
                                    </div>

                                    <!-- Telephone -->
                                    <div v-else-if="question.type === 'telephone'" class="mt-4">
                                        <telephone-input :input-id="`question-tel-${question.id}`"
                                            v-model="telephoneSelections[question.id].telephone"
                                            v-model:country-code="telephoneSelections[question.id].countryCode"
                                            :required="question.is_required" placeholder="Enter telephone number"
                                            @update:modelValue="syncTelephoneAnswer(question.id)"
                                            @update:countryCode="syncTelephoneAnswer(question.id)">
                                        </telephone-input>
                                    </div>

                                    <!-- Email -->
                                    <div v-else-if="question.type === 'email'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="email"
                                            :required="question.is_required" placeholder="Enter email address..."
                                            class="survey-input" />
                                    </div>

                                    <!-- Address -->
                                    <div v-else-if="question.type === 'address'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="text"
                                            :required="question.is_required" placeholder="Enter address..."
                                            class="survey-input" />
                                    </div>

                                    <!-- Country -->
                                    <div v-else-if="question.type === 'country'" class="mt-4">
                                        <multiselect v-model="countrySelections[question.id]" :options="countriesList"
                                            track-by="id" label="country_name" :close-on-select="true" :multiple="false"
                                            placeholder="Select your country" :allow-empty="!question.is_required"
                                            @update:modelValue="onCountrySelected(question.id, $event)">
                                            <template #noResult>
                                                <span class="text-gray-500 text-sm px-2 py-1 block">No countries
                                                    found</span>
                                            </template>
                                        </multiselect>
                                    </div>

                                    <!-- Emirate -->
                                    <div v-else-if="question.type === 'emirate'" class="mt-4">
                                        <multiselect v-model="emirateSelections[question.id]" :options="emiratesList"
                                            :close-on-select="true" :multiple="false" placeholder="Select your emirate"
                                            :allow-empty="!question.is_required"
                                            @update:modelValue="onEmirateSelected(question.id, $event)">
                                            <template #noResult>
                                                <span class="text-gray-500 text-sm px-2 py-1 block">No emirates
                                                    found</span>
                                            </template>
                                        </multiselect>
                                    </div>

                                    <!-- Geo Location -->
                                    <div v-else-if="question.type === 'geo_location'" class="mt-4">
                                        <div class="flex flex-col gap-3">
                                            <div class="flex gap-3 items-stretch">
                                                <input
                                                    :value="geoSelections[question.id] && geoSelections[question.id].address ? geoSelections[question.id].address : ''"
                                                    type="text" readonly :required="question.is_required"
                                                    placeholder="Click to fetch current location..."
                                                    class="survey-input flex-1" />
                                                <button type="button" @click="fetchCurrentLocation(question.id)"
                                                    class="px-4 py-2 bg-btn text-white rounded-lg font-medium">
                                                    Use Current Location
                                                </button>
                                            </div>
                                            <div v-if="geoSelections[question.id] && geoSelections[question.id].lat && geoSelections[question.id].lng"
                                                class="rounded-lg overflow-hidden border border-gray-200">
                                                <iframe
                                                    :src="`https://www.google.com/maps?q=${geoSelections[question.id].lat},${geoSelections[question.id].lng}&z=15&output=embed`"
                                                    width="100%" height="260" style="border:0;" allowfullscreen=""
                                                    loading="lazy"></iframe>
                                            </div>
                                            <p v-if="geoSelections[question.id] && geoSelections[question.id].error"
                                                class="text-xs text-red-600">
                                                {{ geoSelections[question.id].error }}
                                            </p>
                                        </div>
                                    </div>

                                    <!-- Price -->
                                    <div v-else-if="question.type === 'price'" class="mt-4">
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label class="text-xs text-gray-600 mb-1 block">Amount</label>
                                                <input v-model="priceSelections[question.id].amount" type="number"
                                                    placeholder="Enter amount" class="survey-input" />
                                            </div>
                                            <div>
                                                <label class="text-xs text-gray-600 mb-1 block">Currency</label>
                                                <select v-model="priceSelections[question.id].currency"
                                                    class="survey-select">
                                                    <option value="">Select currency</option>
                                                    <option v-for="c in currencyOptions" :key="c" :value="c">{{ c }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Date -->
                                    <div v-else-if="question.type === 'date'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="date"
                                            :required="question.is_required" class="survey-input" />
                                    </div>

                                    <!-- Time -->
                                    <div v-else-if="question.type === 'time'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="time"
                                            :required="question.is_required" class="survey-input" />
                                    </div>

                                    <!-- Date & Time -->
                                    <div v-else-if="question.type === 'date_time'" class="mt-4">
                                        <input v-model="answers[question.id].answer_text" type="datetime-local"
                                            :required="question.is_required" class="survey-input" />
                                    </div>

                                    <!-- Month -->
                                    <div v-else-if="question.type === 'month'" class="mt-4">
                                        <select v-model="answers[question.id].answer_text"
                                            :required="question.is_required"
                                            class="survey-input w-full">
                                            <option value="">Select a month</option>
                                            <option v-for="(monthName, index) in monthOptions" :key="index" :value="monthName">
                                                {{ monthName }}
                                            </option>
                                        </select>
                                    </div>

                                    <!-- Switch (tri-state) -->
                                    <div v-else-if="question.type === 'switch'" class="mt-4">
                                        <base-toggle-3state :value="switchSelections[question.id]"
                                            @update:value="setSwitchValue(question.id, $event)"
                                            :left-label="question.switch_left_label || 'Off'"
                                            :right-label="question.switch_right_label || 'On'" />
                                    </div>

                                    <!-- Radio Buttons -->
                                    <div v-else-if="question.type === 'radio'" class="mt-2">
                                        <div class="">
                                            <label v-for="option in question.options" :key="option.id"
                                                class="survey-radio-option">
                                                <input type="radio" :name="`question-${question.id}`" :value="option.id"
                                                    v-model="answers[question.id].option_id"
                                                    @change="onChoiceOptionChange(question)"
                                                    :required="question.is_required" class="survey-radio-input" />
                                                <span class="survey-radio-label">{{ option.option_text }}</span>
                                            </label>
                                        </div>
                                        <div v-if="shouldShowOtherCommentField(question, answers[question.id])"
                                            class="mt-3">
                                            <input v-model="answers[question.id].answer_text" type="text"
                                                placeholder="Please specify"
                                                class="survey-input w-full"
                                                :required="question.is_required" />
                                        </div>
                                    </div>

                                    <!-- Checkboxes -->
                                    <div v-else-if="question.type === 'checkbox'" class="mt-2">
                                        <div class="space-y-3">
                                            <label v-for="option in question.options" :key="option.id"
                                                class="survey-checkbox-option">
                                                <input type="checkbox" :value="option.id"
                                                    v-model="answers[question.id].option_ids"
                                                    @change="onChoiceOptionChange(question)"
                                                    :required="question.is_required && (!answers[question.id].option_ids || answers[question.id].option_ids.length === 0)"
                                                    class="survey-checkbox-input" />
                                                <span class="survey-checkbox-label">{{ option.option_text }}</span>
                                            </label>
                                        </div>
                                        <div v-if="shouldShowOtherCommentField(question, answers[question.id])"
                                            class="mt-3">
                                            <input v-model="answers[question.id].answer_text" type="text"
                                                placeholder="Please specify"
                                                class="survey-input w-full"
                                                :required="question.is_required" />
                                        </div>
                                    </div>

                                    <!-- Select Dropdown -->
                                    <div v-else-if="question.type === 'select'" class="mt-4">
                                        <multiselect v-model="selectSelections[question.id]" :options="question.options"
                                            track-by="id" label="option_text" :close-on-select="true" :multiple="false"
                                            placeholder="Select an option" :allow-empty="!question.is_required"
                                            @update:modelValue="onSelectOption(question, $event)">
                                            <template #noResult>
                                                <span class="text-gray-500 text-sm px-2 py-1 block">No options
                                                    found</span>
                                            </template>
                                        </multiselect>
                                        <div v-if="shouldShowOtherCommentField(question, answers[question.id])"
                                            class="mt-3">
                                            <input v-model="answers[question.id].answer_text" type="text"
                                                placeholder="Please specify"
                                                class="survey-input w-full"
                                                :required="question.is_required" />
                                        </div>
                                    </div>

                                    <!-- Rating -->
                                    <div v-else-if="question.type === 'rating'" class="mt-4">
                                        <div
                                            class="flex items-center justify-center sm:justify-start space-x-3 sm:space-x-4">
                                            <label v-for="rating in [1, 2, 3, 4, 5]" :key="rating"
                                                class="survey-rating-option"
                                                @mouseenter="setRatingHover(question.id, rating)"
                                                @mouseleave="clearRatingHover(question.id)">
                                                <input type="radio" :name="`question-${question.id}`" :value="rating"
                                                    v-model="answers[question.id].answer_text"
                                                    :required="question.is_required" class="survey-rating-input" />
                                                <span class="survey-rating-label">
                                                    <svg viewBox="0 0 24 24" class="rating-star"
                                                        :style="{ fill: getStarFillColor(question.id, rating) }">
                                                        <path
                                                            d="M12 2.5l2.9 6.07 6.7.97-4.85 4.73 1.15 6.66L12 17.77l-5.9 3.16 1.15-6.66L2.4 9.54l6.7-.97L12 2.5z" />
                                                    </svg>
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Emoji Rating -->
                                    <div v-else-if="question.type === 'emoji_rating'" class="mt-4">
                                        <div
                                            class="flex items-center justify-center sm:justify-start space-x-3 sm:space-x-4 flex-wrap">
                                            <label v-for="(emojiOption, index) in emojiRatingOptions" :key="index"
                                                class="survey-emoji-rating-option">
                                                <input type="radio" :name="`question-${question.id}`"
                                                    :value="emojiOption.value"
                                                    v-model="answers[question.id].answer_text"
                                                    :required="question.is_required"
                                                    class="survey-emoji-rating-input" />
                                                <span class="survey-emoji-rating-label">
                                                    <img :src="emojiOption.svg" :alt="emojiOption.label"
                                                        class="w-8 h-8 sm:w-14 sm:h-14 object-contain" />
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Table -->
                                    <div v-else-if="question.type === 'table'" class="mt-4">
                                        <div class="overflow-x-auto">
                                            <table class="min-w-full border border-gray-300 rounded-lg">
                                                <thead>
                                                    <tr class="bg-gray-50">
                                                        <th v-for="(column, colIndex) in sortedOptions(question.options)"
                                                            :key="colIndex"
                                                            class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                                                            {{ column.option_text }}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(row, rowIndex) in getTableRows(question)"
                                                        :key="rowIndex"
                                                        class="border-b border-gray-200 hover:bg-gray-50">
                                                        <td v-for="(column, colIndex) in sortedOptions(question.options)"
                                                            :key="colIndex"
                                                            class="px-4 py-3 border-r border-gray-200 last:border-r-0">
                                                            <input
                                                                v-model="tableAnswers[question.id][rowIndex][colIndex]"
                                                                type="text" :required="question.is_required"
                                                                :placeholder="`Enter value for ${column.option_text}`"
                                                                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                                                @input="updateTableAnswer(question.id, rowIndex, colIndex, $event.target.value)" />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <!-- File Upload -->
                                    <div v-else-if="question.type === 'file_upload'" class="mt-4">
                                        <div class="flex items-center gap-3 flex-wrap">
                                            <button type="button" @click="triggerFileUpload(question)"
                                                class="px-4 py-2.5 bg-btn text-white rounded-lg text-sm font-medium transition-colors flex items-center hover:opacity-90">
                                                <font-awesome-icon icon="upload" class="mr-2" />
                                                Choose File
                                            </button>
                                            <span v-if="fileUploadNames[question.id]" class="text-sm text-gray-700">
                                                {{ fileUploadNames[question.id] }}
                                            </span>
                                            <button v-if="answers[question.id] && answers[question.id].answer_text" type="button"
                                                @click="clearFileUpload(question.id)"
                                                class="text-red-600 hover:text-red-800 text-sm font-medium">
                                                Remove
                                            </button>
                                        </div>
                                        <p class="text-xs text-gray-500 mt-2">
                                            Max size: {{ question.file_upload_max_size_mb || 2 }} MB.
                                            Allowed: {{ (question.file_upload_types || ['image']).join(', ') }}.
                                        </p>
                                    </div>

                                    <!-- Grid Single Select -->
                                    <div v-else-if="question.type === 'grid_single_select'" class="mt-4">
                                        <div class="overflow-x-auto">
                                            <table class="survey-grid-table min-w-full border rounded-lg">
                                                <thead>
                                                    <tr class="bg-gray-50">
                                                        <!-- Blank cell for row 0, column 0 -->
                                                        <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-r"></th>
                                                        <!-- Column headers (row 0, columns 1+) -->
                                                        <th v-for="(column, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                            class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-r last:border-r-0">
                                                            {{ column.option_text }}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <!-- Data rows (rows 1+) -->
                                                    <tr v-for="(rowTitle, rowIndex) in getGridRowTitles(question)" :key="rowIndex"
                                                        class="border-b hover:bg-gray-50">
                                                        <!-- Row title (column 0) -->
                                                        <td class="px-4 py-3 text-sm font-semibold text-gray-700 border-r">
                                                            {{ rowTitle }}
                                                        </td>
                                                        <!-- Radio buttons for each column -->
                                                        <td v-for="(column, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                            class="px-4 py-3 border-r last:border-r-0 text-center">
                                                            <label class="inline-flex items-center cursor-pointer">
                                                                <input 
                                                                    type="radio" 
                                                                    :name="`grid-${question.id}-row-${rowIndex}`"
                                                                    :value="column.id"
                                                                    v-model="gridAnswers[question.id][rowIndex]"
                                                                    @change="updateGridAnswer(question.id, rowIndex, column.id)"
                                                                    :required="question.is_required"
                                                                    class="survey-radio-input" />
                                                                <span class="survey-radio-label"></span>
                                                            </label>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <!-- Grid Multiple Select Question -->
                                    <div v-else-if="question.type === 'grid_multiple_select'" class="mt-4 survey-grid-multiple-wrap">
                                        <div class="overflow-x-auto">
                                            <table class="survey-grid-table min-w-full border rounded-lg">
                                                <thead>
                                                    <tr class="bg-gray-50">
                                                        <!-- Blank cell for row 0, column 0 -->
                                                        <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-r"></th>
                                                        <!-- Column headers (row 0, columns 1+) -->
                                                        <th v-for="(column, colIndex) in sortedOptions(question.options)" :key="colIndex"
                                                            class="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-r last:border-r-0">
                                                            {{ column.option_text }}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <!-- Data rows (rows 1+) -->
                                                    <tr v-for="(rowTitle, rowIndex) in getGridRowTitles(question)" :key="'grid-multi-' + question.id + '-row-' + rowIndex"
                                                        class="border-b hover:bg-gray-50">
                                                        <!-- Row title (column 0) -->
                                                        <td class="px-4 py-3 text-sm font-semibold text-gray-700 border-r">
                                                            {{ rowTitle }}
                                                        </td>
                                                        <!-- Checkboxes for each column (manual checked/change to avoid v-model selecting all) -->
                                                        <td v-for="column in sortedOptions(question.options)" :key="'col-' + column.id"
                                                            class="px-4 py-3 border-r last:border-r-0 text-center">
                                                            <label class="inline-flex items-center cursor-pointer">
                                                                <input 
                                                                    type="checkbox" 
                                                                    :checked="isGridMultipleOptionSelected(question.id, rowIndex, column.id)"
                                                                    @change="toggleGridMultipleOption(question.id, rowIndex, column.id)"
                                                                    class="survey-checkbox-input" />
                                                                <span class="survey-checkbox-label"></span>
                                                            </label>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <!-- Signature -->
                                    <div v-else-if="question.type === 'signature'" class="mt-4">
                                        <div class="survey-signature-container rounded-lg bg-white relative" 
                                            :class="{ 
                                                'border-red-500': question.is_required && (!answers[question.id] || !answers[question.id].answer_text),
                                                'survey-signature-active': signatureDrawing[question.id] && signatureDrawing[question.id].isDrawing
                                            }">
                                            <canvas 
                                                :ref="`signatureCanvas_${question.id}`"
                                                :data-question-id="question.id"
                                                @mousedown="startSignature(question.id, $event)"
                                                @mousemove="drawSignature(question.id, $event)"
                                                @mouseup="stopSignature(question.id)"
                                                @mouseleave="stopSignature(question.id)"
                                                @touchstart.prevent="startSignature(question.id, $event)"
                                                @touchmove.prevent="drawSignature(question.id, $event)"
                                                @touchend.prevent="stopSignature(question.id)"
                                                class="w-full cursor-crosshair"
                                                style="touch-action: none;"
                                                :width="signatureCanvasWidth"
                                                :height="200"></canvas>
                                            <div v-if="!answers[question.id] || !answers[question.id].answer_text" 
                                                class="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <p class="text-gray-400 text-sm">Sign here</p>
                                            </div>
                                        </div>
                                        <div class="flex items-center gap-2 mt-3">
                                            <button type="button" @click="clearSignature(question.id)"
                                                class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                                                Clear
                                            </button>
                                            <span v-if="answers[question.id] && answers[question.id].answer_text" 
                                                class="text-sm text-green-600 font-medium flex items-center">
                                                <font-awesome-icon icon="check-circle" class="mr-1" />
                                                Signed
                                            </span>
                                        </div>
                                    </div>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <!-- Contact Information Section for anonymous users on public surveys -->
                        <div v-if="showContactInformationForm && showContactOnCurrentStep" class="mt-8 pt-8 border-t border-gray-200">
                            <div class="mb-4">
                                <p class="text-base sm:text-lg text-gray-800 leading-relaxed mb-2"
                                    style="color: #b47c2c;">
                                    This survey is anonymous. However, if you would like us to contact you for follow-up
                                    or to recognize your
                                    feedback, please share your name and contact details below.
                                </p>
                                <p class="text-sm text-gray-600 italic">
                                    (This information will be kept confidential and used only for communication related
                                    to your feedback.)
                                </p>
                            </div>

                            <div class="space-y-4 mt-6">
                                <!-- Name Field -->
                                <div class="flex flex-col">
                                    <label for="contact-name" class="text-sm font-semibold text-gray-700 mb-2">
                                        Name
                                    </label>
                                    <input id="contact-name" v-model="contactInformation.name" type="text"
                                        placeholder="Enter your name" class="survey-input" />
                                </div>

                                <!-- Email Field -->
                                <div class="flex flex-col">
                                    <label for="contact-email" class="text-sm font-semibold text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input id="contact-email" v-model="contactInformation.email" type="email"
                                        placeholder="Enter your email" class="survey-input" />
                                </div>

                                <!-- Telephone Field -->
                                <div class="flex flex-col">
                                    <label for="contact-telephone" class="text-sm font-semibold text-gray-700 mb-2">
                                        Telephone
                                    </label>
                                    <telephone-input id="contact-telephone" v-model="contactInformation.telephone"
                                        v-model:country-code="contactInformation.countryCode"
                                        placeholder="Enter your telephone number"
                                        input-id="contact-telephone"></telephone-input>
                                </div>
                            </div>
                        </div>
                    </form>

                    <!-- Success Message -->
                    <div v-else
                        class="bg-white rounded-xl shadow-lg border border-gray-100 p-8 sm:p-12 text-center mt-4">
                        <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                            <font-awesome-icon icon="check-circle" class="text-5xl text-green-500" />
                        </div>
                        <h2 class="text-3xl font-bold text-gray-900 mb-3">Thank You!</h2>
                        <p class="text-gray-600 text-lg leading-relaxed mb-6">
                            Your response has been submitted successfully. We appreciate your time and feedback.
                        </p>
                        <div class="inline-flex items-center space-x-2 text-color-1">
                            <font-awesome-icon icon="heart" class="text-xl" />
                            <span class="font-medium">Your feedback matters to us</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="z-30 fixed bottom-0 w-full ht-auto sm:ht-55 flex flex-col sm:flex-row box-shadow-1" v-if="survey">
            <div class="flex flex-1 bg-white justify-center px-3 sm:px-0 py-1.5 sm:py-0">
                <div class="flex flex-col justify-center items-center text-center">
                    <span class="text-xxxs leading-none font-medium text-gray-400">Powered by</span>
                    <span class="text-primary font-bold text-xl sm:text-2xl leading-none">Human Centric Ecosystem
                        Platform</span>
                </div>
            </div>
            <!-- Survey navigation / submit -->
            <div v-if="!submitted && hasPagedSurveyFlow && totalPages > 1"
                class="w-full sm:w-auto flex flex-col sm:flex-row">
                <button type="button" :disabled="isFirstSurveyPage" @click.stop="goToPreviousSurveyPage"
                    class="survey-nav-btn survey-nav-btn-prev"
                    :class="{ 'survey-nav-btn-disabled': isFirstSurveyPage }">
                    <font-awesome-icon icon="chevron-left" class="text-xl" />
                    <span>Previous</span>
                </button>
                <button v-if="!isLastSurveyPage" type="button" @click.stop="goToNextSurveyPage"
                    class="survey-nav-btn survey-nav-btn-next">
                    <span>Next</span>
                    <font-awesome-icon icon="chevron-right" class="text-xl" />
                </button>
                <button v-else type="button" @click.stop="submitResponse"
                    class="survey-nav-btn survey-nav-btn-submit">
                    <span>Submit</span>
                    <font-awesome-icon icon="chevron-right" class="text-xl" />
                </button>
            </div>
            <div v-else-if="!submitted" @click.stop="submitResponse"
                class="w-full sm:w-60 bg-btn text-white uppercase px-3 py-1.5 flex justify-between items-center cursor-pointer">
                <div class="flex-1 text-center">
                    <span>Submit</span>
                </div>
                <font-awesome-icon icon="chevron-right" class="text-2xl" />
            </div>
        </div>

        <div class="min-h-screen flex justify-center items-center" v-if="loading || submitting">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

    </div>
</template>

<script>
import { gql } from '@apollo/client/core';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import TelephoneInput from '@/components/TelephoneInput.vue';
import {
    getSectionTitleColor,
    getPageTitleColor,
    getPageTitleBorderWidth,
    getPageTitleBorderColor,
    getSectionHeaderBorderStyle as buildSectionHeaderBorderStyle,
    DEFAULT_PAGE_TITLE_COLOR,
    DEFAULT_PAGE_TITLE_BORDER_WIDTH,
    DEFAULT_PAGE_TITLE_BORDER_COLOR,
} from '@/utils/surveySectionColor';
import {
    isOtherOptionSelected,
    shouldShowOtherCommentField,
} from '@/utils/surveyOtherOption';

const GET_ALL_COUNTRIES = gql`
  query GetAllCountries {
    countries {
      id
      country_name
      country_code
    }
  }
`;

const GET_SURVEY_BY_SLUG = gql`
  query GetSurveyBySlug($slug: String!, $forParticipation: Boolean) {
    surveyBySlug(slug: $slug, forParticipation: $forParticipation) {
      error
      error_code
      message
      data {
        id
        title
        display_title
        description
        slug
        is_active
        visibility
        collect_contact_information
        thumbnail_path
        thumbnail_url
        questions {
          id
          question_text
          type
          is_required
          order
          hidden_by_default
          table_rows
          grid_row_titles
          file_upload_max_size_mb
          file_upload_types
          switch_left_label
          switch_right_label
          section_title_color
          section_title_border_width
          section_title_border_color
          page_title_color
          page_title_border_width
          page_title_border_color
          add_other_option
          other_option_comment_enabled
          options {
            id
            option_text
            value
            order
          }
          logic_rules {
            id
            operator
            value
            action
            target_question_ids
            order
          }
        }
      }
    }
  }
`;

const SUBMIT_SURVEY_RESPONSE = gql`
  mutation SubmitSurveyResponse($input: SurveyResponseInput!) {
    submitSurveyResponse(input: $input) {
      error
      error_code
      message
      data {
        id
        token
        status
      }
    }
  }
`;

const SAVE_SURVEY_PROGRESS = gql`
  mutation SaveSurveyProgress($input: SaveSurveyProgressInput!) {
    saveSurveyProgress(input: $input) {
      error
      error_code
      message
      data {
        id
        token
        status
        completion_percentage
        answered_questions_count
        total_questions_count
      }
    }
  }
`;

const GET_SURVEY_RESPONSE_BY_TOKEN = gql`
  query GetSurveyResponseByToken($survey_id: ID!, $token: String!) {
    surveyResponseByToken(survey_id: $survey_id, token: $token) {
      error
      error_code
      message
      data {
        id
        token
        status
        is_submitted
        completion_percentage
        answers {
          id
          question_id
          answer_text
          option_id
          option_ids
        }
      }
    }
  }
`;

const UPLOAD_SURVEY_FILE = gql`
  mutation UploadSurveyFile($survey_id: ID!, $question_id: ID!, $file: Upload!) {
    uploadSurveyFile(survey_id: $survey_id, question_id: $question_id, file: $file) {
      error
      error_code
      message
      path
    }
  }
`;

export default {
    name: 'TakeSurvey',
    components: {
        Multiselect,
        TelephoneInput,
    },
    data() {
        return {
            survey: null,
            loading: true,
            error: null,
            answers: {},
            submitting: false,
            submitted: false,
            ratingHover: {},
            selectSelections: {},
            telephoneSelections: {},
            countriesList: [],
            countrySelections: {},
            emirateSelections: {},
            geoSelections: {},
            priceSelections: {},
            switchSelections: {},
            tableAnswers: {},
            gridAnswers: {},
            gridMultipleAnswers: {},
            currentFileUploadQuestion: null,
            fileUploadNames: {},
            signatureDrawing: {},
            signatureCanvasWidth: 600,
            emiratesList: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'],
            currencyOptions: ['AED', 'USD', 'SAR', 'EUR', 'GBP', 'KWD', 'QAR', 'OMR', 'BHD'],
            contactInformation: {
                name: '',
                email: '',
                telephone: '',
                countryCode: '+971',
            },
            responseToken: null,
            restoringProgress: false,
            progressSaveTimer: null,
            progressSaving: false,
            currentPageIndex: 0,
        };
    },
    computed: {
        sortedQuestions() {
            if (!this.survey || !this.survey.questions) return [];
            const questions = [...this.survey.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
            const visible = {};
            questions.forEach((q) => {
                visible[String(q.id)] = !q.hidden_by_default;
            });
            questions.forEach((sourceQ) => {
                const rules = sourceQ.logic_rules || [];
                const answer = this.answers[sourceQ.id];
                rules.forEach((rule) => {
                    if (!this.logicRuleConditionMatches(rule, sourceQ, answer)) return;
                    const action = rule.action === 'show';
                    (rule.target_question_ids || []).forEach((tid) => {
                        visible[String(tid)] = action;
                    });
                });
            });
            return questions.filter((q) => visible[String(q.id)]);
        },
        hasPagedSurveyFlow() {
            const questions = this.sortedQuestions || [];
            for (let i = 0; i < questions.length; i++) {
                if (questions[i].type === 'page_break') {
                    return true;
                }
            }
            return false;
        },
        surveyPageGroups() {
            const questions = this.sortedQuestions || [];
            const pages = [];
            let pendingTitle = null;
            let pendingTitleColor = DEFAULT_PAGE_TITLE_COLOR;
            let pendingTitleBorderWidth = DEFAULT_PAGE_TITLE_BORDER_WIDTH;
            let pendingTitleBorderColor = DEFAULT_PAGE_TITLE_BORDER_COLOR;
            let currentQuestions = [];

            for (let i = 0; i < questions.length; i++) {
                const q = questions[i];
                if (q.type === 'page_break') {
                    if (currentQuestions.length > 0) {
                        pages.push({
                            title: pendingTitle,
                            titleColor: pendingTitleColor,
                            titleBorderWidth: pendingTitleBorderWidth,
                            titleBorderColor: pendingTitleBorderColor,
                            questions: currentQuestions,
                        });
                        currentQuestions = [];
                    }
                    const text = q.question_text ? q.question_text.trim() : '';
                    pendingTitle = text || null;
                    pendingTitleColor = getPageTitleColor(q);
                    pendingTitleBorderWidth = getPageTitleBorderWidth(q);
                    pendingTitleBorderColor = getPageTitleBorderColor(q);
                    continue;
                }
                currentQuestions.push(q);
            }

            if (currentQuestions.length > 0) {
                pages.push({
                    title: pendingTitle,
                    titleColor: pendingTitleColor,
                    titleBorderWidth: pendingTitleBorderWidth,
                    titleBorderColor: pendingTitleBorderColor,
                    questions: currentQuestions,
                });
            }

            if (pages.length === 0) {
                return [{
                    title: null,
                    titleColor: DEFAULT_PAGE_TITLE_COLOR,
                    titleBorderWidth: DEFAULT_PAGE_TITLE_BORDER_WIDTH,
                    titleBorderColor: DEFAULT_PAGE_TITLE_BORDER_COLOR,
                    questions: questions.filter((item) => item.type !== 'page_break'),
                }];
            }

            return pages;
        },
        totalPages() {
            return this.surveyPageGroups.length;
        },
        isFirstSurveyPage() {
            return this.currentPageIndex <= 0;
        },
        isLastSurveyPage() {
            return this.currentPageIndex >= this.totalPages - 1;
        },
        currentPageTitle() {
            if (!this.hasPagedSurveyFlow) {
                return '';
            }
            const page = this.surveyPageGroups[this.currentPageIndex];
            return page && page.title ? page.title : '';
        },
        currentPageTitleColor() {
            if (!this.hasPagedSurveyFlow) {
                return DEFAULT_PAGE_TITLE_COLOR;
            }
            const page = this.surveyPageGroups[this.currentPageIndex];
            if (!page || !page.title) {
                return DEFAULT_PAGE_TITLE_COLOR;
            }
            return page.titleColor || DEFAULT_PAGE_TITLE_COLOR;
        },
        currentPageTitleBorderStyle() {
            if (!this.hasPagedSurveyFlow || !this.currentPageTitle) {
                return {};
            }
            const page = this.surveyPageGroups[this.currentPageIndex];
            const width = page && page.titleBorderWidth !== undefined && page.titleBorderWidth !== null
                ? page.titleBorderWidth
                : DEFAULT_PAGE_TITLE_BORDER_WIDTH;
            const color = page && page.titleBorderColor
                ? page.titleBorderColor
                : DEFAULT_PAGE_TITLE_BORDER_COLOR;
            if (width === 0) {
                return { borderBottom: 'none' };
            }
            return {
                borderBottomWidth: width + 'px',
                borderBottomStyle: 'solid',
                borderBottomColor: color,
            };
        },
        questionsToDisplay() {
            if (this.hasPagedSurveyFlow) {
                const page = this.surveyPageGroups[this.currentPageIndex];
                return page && page.questions ? page.questions : [];
            }
            return this.sortedQuestions;
        },
        currentPageQuestions() {
            if (!this.hasPagedSurveyFlow) {
                return this.sortedQuestions.filter((q) => !this.isStructuralQuestionType(q.type));
            }
            const page = this.surveyPageGroups[this.currentPageIndex];
            if (!page || !page.questions) {
                return [];
            }
            return page.questions.filter((q) => !this.isStructuralQuestionType(q.type));
        },
        showContactOnCurrentStep() {
            if (!this.hasPagedSurveyFlow || this.totalPages <= 1) {
                return true;
            }
            return this.isLastSurveyPage;
        },
        surveyThumbnail() {
            if (!this.survey || !this.survey.thumbnail_url) return '';
            return this.getFullImageUrl(this.survey.thumbnail_url);
        },
        shouldShowTitle() {
            if (!this.survey || !this.survey.title) {
                return false;
            }
            if (this.survey.display_title === undefined || this.survey.display_title === null) {
                return true;
            }
            return this.survey.display_title;
        },
        isLoggedIn() {
            return this.$store.getters.isAuthenticated;
        },
        showContactInformationForm() {
            return this.survey
                && this.survey.visibility === 'public'
                && !this.isLoggedIn
                && this.survey.collect_contact_information !== false;
        },
        answerableQuestions() {
            if (!this.sortedQuestions || this.sortedQuestions.length === 0) return [];
            return this.sortedQuestions.filter(q => !this.isStructuralQuestionType(q.type));
        },
        progressPercentage() {
            if (!this.survey || !this.survey.questions || this.survey.questions.length === 0) {
                return 0;
            }
            const answerable = this.answerableQuestions;
            if (answerable.length === 0) return 100;
            let answeredCount = 0;
            answerable.forEach(question => {
                const answer = this.answers[question.id];
                if (!answer) {
                    return;
                }
                let isAnswered = false;
                if (question.type === 'checkbox') {
                    isAnswered = answer.option_ids && answer.option_ids.length > 0;
                } else if (['radio', 'select'].includes(question.type)) {
                    isAnswered = answer.option_id !== null && answer.option_id !== undefined;
                } else if (question.type === 'table') {
                    // Check if at least one cell is filled
                    const tableData = this.tableAnswers[question.id];
                    if (tableData && Array.isArray(tableData)) {
                        isAnswered = tableData.some(row =>
                            Array.isArray(row) && row.some(cell => cell && cell.toString().trim() !== '')
                        );
                    }
                } else if (question.type === 'file_upload') {
                    isAnswered = answer.answer_text && answer.answer_text.toString().trim() !== '';
                } else if (question.type === 'signature') {
                    isAnswered = answer.answer_text && answer.answer_text.toString().trim() !== '';
                } else if (question.type === 'grid_single_select') {
                    // Check if at least one row has a selection
                    const gridData = this.gridAnswers[question.id];
                    if (gridData && typeof gridData === 'object') {
                        isAnswered = Object.values(gridData).some(val => val !== null && val !== undefined);
                    } else {
                        isAnswered = answer.answer_text && answer.answer_text.toString().trim() !== '' && answer.answer_text !== '{}';
                    }
                } else if (question.type === 'grid_multiple_select') {
                    // Check if at least one row has at least one selection
                    const gridData = this.gridMultipleAnswers[question.id];
                    if (gridData && typeof gridData === 'object') {
                        isAnswered = Object.values(gridData).some(val => Array.isArray(val) && val.length > 0);
                    } else {
                        isAnswered = answer.answer_text && answer.answer_text.toString().trim() !== '' && answer.answer_text !== '{}';
                    }
                } else {
                    // text, textarea, rating, emoji_rating all use answer_text
                    if (question.type === 'rating' || question.type === 'emoji_rating') {
                        isAnswered = answer.answer_text !== null && answer.answer_text !== undefined && answer.answer_text !== '';
                    } else {
                        isAnswered = answer.answer_text && answer.answer_text.toString().trim() !== '';
                    }
                }
                if (isAnswered) {
                    answeredCount++;
                }
            });
            return (answeredCount / answerable.length) * 100;
        },
        emojiRatingOptions() {
            return [
                { svg: require('@/assets/images/survey/emoji_rating/1.svg'), label: 'Very Sad', value: '1' },
                { svg: require('@/assets/images/survey/emoji_rating/2.svg'), label: 'Sad', value: '2' },
                { svg: require('@/assets/images/survey/emoji_rating/3.svg'), label: 'Neutral', value: '3' },
                { svg: require('@/assets/images/survey/emoji_rating/4.svg'), label: 'Happy', value: '4' },
                { svg: require('@/assets/images/survey/emoji_rating/5.svg'), label: 'Very Happy', value: '5' },
            ];
        },
        monthOptions() {
            return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        },
    },
    watch: {
        answers: {
            handler() {
                if (this.survey && this.survey.questions) {
                    this.ensureAnswersForVisibleQuestions();
                }
                if (!this.restoringProgress && this.survey && !this.submitted && !this.loading) {
                    this.scheduleProgressSave();
                }
            },
            deep: true,
            flush: 'sync',
        },
        surveyPageGroups: {
            handler() {
                if (this.currentPageIndex >= this.totalPages) {
                    this.currentPageIndex = Math.max(0, this.totalPages - 1);
                }
            },
            deep: true,
        },
    },
    created() {
        this.loadSurvey();
    },
    mounted() {
        // Set signature canvas width responsively
        this.updateSignatureCanvasWidth();
        window.addEventListener('resize', this.updateSignatureCanvasWidth);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateSignatureCanvasWidth);
        if (this.progressSaveTimer) {
            clearTimeout(this.progressSaveTimer);
            this.progressSaveTimer = null;
        }
        if (this.survey && this.responseToken && !this.submitted) {
            this.saveProgressNow();
        }
    },
    methods: {
        getSectionTitleColor(question) {
            return getSectionTitleColor(question);
        },
        sectionHeaderBorderStyleForQuestion(question) {
            return buildSectionHeaderBorderStyle(question);
        },
        isStructuralQuestionType(type) {
            return type === 'section_header' || type === 'page_break';
        },
        getOptionLogicValue(option, index) {
            if (!option) return '';
            if (option.value != null && option.value !== '') return String(option.value);
            return String(option.option_text != null && option.option_text !== '' ? option.option_text : index);
        },
        resolveStoredLogicExpected(question, expected) {
            if (!question || !question.options || !question.options.length) return String(expected);
            const str = String(expected);
            for (let i = 0; i < question.options.length; i++) {
                const option = question.options[i];
                const logicVal = this.getOptionLogicValue(option, i);
                if (String(option.id) === str || logicVal === str || String(option.option_text) === str) {
                    return logicVal;
                }
            }
            return str;
        },
        parseLogicExpectedArray(expected, question) {
            let expArr = [];
            if (expected == null) return expArr;
            if (typeof expected === 'string') {
                try {
                    expArr = expected.startsWith('[') ? JSON.parse(expected) : [expected];
                } catch (_error) {
                    expArr = [expected];
                }
            } else if (Array.isArray(expected)) {
                expArr = expected;
            } else {
                expArr = [expected];
            }
            if (question) {
                return expArr.map((entry) => this.resolveStoredLogicExpected(question, entry));
            }
            return expArr.map((entry) => String(entry));
        },
        mapOptionIdsToLogicValues(question, optionIds) {
            if (!Array.isArray(optionIds) || !optionIds.length) return [];
            if (!question.options || !question.options.length) {
                return optionIds.map((id) => String(id));
            }
            return optionIds.map((oid) => {
                const opt = question.options.find((o) => String(o.id) === String(oid));
                if (!opt) return String(oid);
                const idx = question.options.findIndex((o) => String(o.id) === String(oid));
                return this.getOptionLogicValue(opt, idx);
            });
        },
        collectGridMultipleOptionIds(question) {
            const allIds = [];
            const gridData = this.gridMultipleAnswers[question.id];
            if (gridData && typeof gridData === 'object') {
                Object.keys(gridData).forEach((key) => {
                    const row = gridData[key];
                    if (Array.isArray(row)) {
                        row.forEach((id) => allIds.push(id));
                    }
                });
            }
            if (!allIds.length && this.answers[question.id] && this.answers[question.id].answer_text) {
                try {
                    const parsed = JSON.parse(this.answers[question.id].answer_text);
                    Object.keys(parsed).forEach((key) => {
                        const row = parsed[key];
                        if (Array.isArray(row)) {
                            row.forEach((id) => allIds.push(id));
                        }
                    });
                } catch (_error) {
                    // Ignore invalid saved grid data
                }
            }
            const uniqueIds = [];
            allIds.forEach((id) => {
                const str = String(id);
                if (uniqueIds.indexOf(str) === -1) uniqueIds.push(str);
            });
            return uniqueIds;
        },
        collectGridSingleLogicValues(question) {
            const values = [];
            const collectFromGrid = (data) => {
                if (!data || typeof data !== 'object') return;
                Object.keys(data).forEach((key) => {
                    const optionId = data[key];
                    if (optionId == null) return;
                    const opt = question.options && question.options.find((o) => String(o.id) === String(optionId));
                    if (opt) {
                        const idx = question.options.findIndex((o) => String(o.id) === String(optionId));
                        values.push(this.getOptionLogicValue(opt, idx));
                    } else {
                        values.push(String(optionId));
                    }
                });
            };
            collectFromGrid(this.gridAnswers[question.id]);
            if (!values.length && this.answers[question.id] && this.answers[question.id].answer_text) {
                try {
                    collectFromGrid(JSON.parse(this.answers[question.id].answer_text));
                } catch (_error) {
                    // Ignore invalid saved grid data
                }
            }
            return values;
        },
        getAnswerValueForLogic(question, answer) {
            if (!answer) {
                if (question.type === 'checkbox' || question.type === 'grid_multiple_select' || question.type === 'grid_single_select') return [];
                return null;
            }
            if (['radio', 'select', 'switch'].includes(question.type)) {
                const optId = answer.option_id;
                if (optId != null && question.options && question.options.length) {
                    const opt = question.options.find((o) => String(o.id) === String(optId));
                    if (opt) {
                        const idx = question.options.findIndex((o) => String(o.id) === String(optId));
                        return this.getOptionLogicValue(opt, idx);
                    }
                    return answer.answer_text;
                }
                return answer.answer_text;
            }
            if (question.type === 'checkbox') {
                return this.mapOptionIdsToLogicValues(question, answer.option_ids || []);
            }
            if (question.type === 'grid_multiple_select') {
                return this.mapOptionIdsToLogicValues(question, this.collectGridMultipleOptionIds(question));
            }
            if (question.type === 'grid_single_select') {
                return this.collectGridSingleLogicValues(question);
            }
            return answer.answer_text;
        },
        logicRuleConditionMatches(rule, sourceQuestion, answer) {
            const operator = rule.operator;
            const expected = rule.value;
            const current = this.getAnswerValueForLogic(sourceQuestion, answer);
            const isMulti = Array.isArray(current);

            switch (operator) {
                case 'equal_to': {
                    const resolvedExpected = this.resolveStoredLogicExpected(sourceQuestion, expected);
                    return isMulti ? current.includes(resolvedExpected) : String(current) === resolvedExpected;
                }
                case 'not_equal_to': {
                    const resolvedExpected = this.resolveStoredLogicExpected(sourceQuestion, expected);
                    return isMulti ? !current.includes(resolvedExpected) : String(current) !== resolvedExpected;
                }
                case 'greater_than':
                    return current != null && expected != null && Number(current) > Number(expected);
                case 'less_than':
                    return current != null && expected != null && Number(current) < Number(expected);
                case 'greater_than_or_equal':
                    return current != null && expected != null && Number(current) >= Number(expected);
                case 'less_than_or_equal':
                    return current != null && expected != null && Number(current) <= Number(expected);
                case 'contains':
                    return current != null && String(current).toLowerCase().includes(String(expected).toLowerCase());
                case 'not_contains':
                    return current == null || String(current).toLowerCase().includes(String(expected).toLowerCase()) === false;
                case 'is_empty':
                    return current == null || current === '' || (isMulti && current.length === 0);
                case 'is_not_empty':
                    return current != null && current !== '' && (!isMulti || current.length > 0);
                case 'contains_any': {
                    if (!isMulti || expected == null) return false;
                    const expArr = this.parseLogicExpectedArray(expected, sourceQuestion);
                    return expArr.some((entry) => current.includes(entry));
                }
                case 'not_contains_any': {
                    if (!isMulti || expected == null) return true;
                    const expArr = this.parseLogicExpectedArray(expected, sourceQuestion);
                    return !expArr.some((entry) => current.includes(entry));
                }
                case 'contains_all': {
                    if (!isMulti || expected == null) return false;
                    const expArr = this.parseLogicExpectedArray(expected, sourceQuestion);
                    return expArr.every((entry) => current.includes(entry));
                }
                default:
                    return false;
            }
        },
        setSwitchValue(questionId, value) {
            if (!questionId) {
                return;
            }
            // Keep tri-state: null / 0 / 1
            const val = (value === 0 || value === 1) ? value : null;
            this.switchSelections = Object.assign({}, this.switchSelections, { [questionId]: val });
            if (this.answers[questionId]) {
                // Store as string for submission (center/null -> empty string)
                this.answers[questionId].answer_text = val === null ? '' : String(val);
            }
        },
        questionNumberForDisplay(index) {
            const question = this.questionsToDisplay[index];
            if (!question) {
                return index + 1;
            }
            const globalIndex = this.sortedQuestions.findIndex((q) => String(q.id) === String(question.id));
            return this.questionNumberForSortedIndex(globalIndex >= 0 ? globalIndex : index);
        },
        questionNumberForSortedIndex(index) {
            let count = 0;
            for (let i = 0; i <= index && i < this.sortedQuestions.length; i++) {
                if (!this.isStructuralQuestionType(this.sortedQuestions[i].type)) {
                    count++;
                }
            }
            return count;
        },
        scrollToSurveyTop() {
            this.$nextTick(() => {
                setTimeout(() => {
                    const layout = this.$route.meta.layout;
                    if (layout === 'dashboard') {
                        const scrollContainer = document.getElementById('dashboard-scroll-container');
                        if (scrollContainer) {
                            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                            return;
                        }
                    }
                    const scrollContainer = document.getElementById('default-scroll-container');
                    if (scrollContainer) {
                        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }, 50);
            });
        },
        goToPreviousSurveyPage() {
            if (this.isFirstSurveyPage) {
                return;
            }
            this.currentPageIndex -= 1;
            this.scrollToSurveyTop();
        },
        goToNextSurveyPage() {
            if (!this.validateQuestionsForNavigation(this.currentPageQuestions)) {
                return;
            }
            if (!this.isLastSurveyPage) {
                this.currentPageIndex += 1;
                this.scrollToSurveyTop();
            }
        },
        validateQuestionsForNavigation(questions) {
            if (!Array.isArray(questions) || !questions.length) {
                return true;
            }

            for (let qIndex = 0; qIndex < questions.length; qIndex++) {
                const question = questions[qIndex];
                if (this.isStructuralQuestionType(question.type)) {
                    continue;
                }

                if (question.type === 'price') {
                    const item = this.priceSelections && this.priceSelections[question.id] ? this.priceSelections[question.id] : null;
                    const amount = item && item.amount !== undefined && item.amount !== null ? item.amount.toString().trim() : '';
                    const currency = item && item.currency ? item.currency.toString().trim() : '';
                    const hasAny = amount !== '' || currency !== '';
                    if ((question.is_required || hasAny) && (amount === '' || currency === '')) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please enter amount and currency for: ${question.question_text}`,
                        });
                        return false;
                    }
                    if ((question.is_required || hasAny) && this.answers[question.id]) {
                        this.answers[question.id].answer_text = JSON.stringify({ amount: amount, currency: currency });
                    }
                }

                if (question.type === 'date_time') {
                    const answer = this.answers[question.id];
                    const raw = answer && answer.answer_text !== undefined && answer.answer_text !== null
                        ? answer.answer_text.toString().trim()
                        : '';
                    const hasAny = raw !== '';
                    if (question.is_required && !hasAny) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please select date and time for: ${question.question_text}`,
                        });
                        return false;
                    }
                    if (hasAny) {
                        const normalized = this.normalizeDateTimeToSql(raw);
                        if (!normalized) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Invalid date/time format for: ${question.question_text}`,
                            });
                            return false;
                        }
                        this.answers[question.id].answer_text = normalized;
                    }
                }
            }

            for (let qIndex = 0; qIndex < questions.length; qIndex++) {
                const question = questions[qIndex];
                if (this.isStructuralQuestionType(question.type) || !question.is_required) {
                    continue;
                }

                const answer = this.answers[question.id];
                if (question.type === 'checkbox') {
                    if (!answer || !answer.option_ids || answer.option_ids.length === 0) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please answer: ${question.question_text}`,
                        });
                        return false;
                    }
                    if (shouldShowOtherCommentField(question, answer)
                        && (!answer.answer_text || !answer.answer_text.toString().trim())) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please specify your Other response for: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (['radio', 'select'].includes(question.type)) {
                    if (!answer || !answer.option_id) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please answer: ${question.question_text}`,
                        });
                        return false;
                    }
                    if (shouldShowOtherCommentField(question, answer)
                        && (!answer.answer_text || !answer.answer_text.toString().trim())) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please specify your Other response for: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (question.type === 'table') {
                    const tableData = this.tableAnswers[question.id];
                    const hasAnswer = tableData && Array.isArray(tableData) && tableData.some((row) =>
                        Array.isArray(row) && row.some((cell) => cell && cell.toString().trim() !== '')
                    );
                    if (!hasAnswer) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please fill at least one cell in: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (question.type === 'grid_single_select') {
                    const gridData = this.gridAnswers[question.id];
                    const hasSelection = gridData && typeof gridData === 'object'
                        && Object.values(gridData).some((val) => val !== null && val !== undefined);
                    if (!hasSelection) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please answer: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (question.type === 'grid_multiple_select') {
                    const gridData = this.gridMultipleAnswers[question.id];
                    const hasSelection = gridData && typeof gridData === 'object'
                        && Object.values(gridData).some((val) => Array.isArray(val) && val.length > 0);
                    if (!hasSelection) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please answer: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (question.type === 'switch') {
                    const switchValue = this.switchSelections[question.id];
                    if (switchValue === null || switchValue === undefined) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please select an option for: ${question.question_text}`,
                        });
                        return false;
                    }
                    if (this.answers[question.id]) {
                        this.answers[question.id].answer_text = String(switchValue);
                    }
                } else if (question.type === 'rating' || question.type === 'emoji_rating') {
                    if (!answer || answer.answer_text === null || answer.answer_text === undefined || answer.answer_text === '') {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please answer: ${question.question_text}`,
                        });
                        return false;
                    }
                } else if (!answer || !answer.answer_text || (typeof answer.answer_text === 'string' && answer.answer_text.trim() === '')) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: `Please answer: ${question.question_text}`,
                    });
                    return false;
                }
            }

            return true;
        },
        ensureAnswersForVisibleQuestions() {
            const questions = this.sortedQuestions || [];
            questions.forEach((q) => {
                if (this.isStructuralQuestionType(q.type)) return;
                if (!this.answers[q.id]) {
                    this.ensureAnswerForQuestion(q);
                }
            });
        },
        ensureAnswerForQuestion(question) {
            if (this.isStructuralQuestionType(question.type)) return;
            if (question.type === 'checkbox') {
                this.answers[question.id] = { question_id: question.id, option_ids: [], answer_text: '' };
            } else if (['radio', 'select'].includes(question.type)) {
                this.answers[question.id] = { question_id: question.id, option_id: null, answer_text: '' };
            } else {
                this.answers[question.id] = { question_id: question.id, answer_text: '' };
            }
            if (question.type === 'telephone') {
                if (!this.telephoneSelections[question.id]) {
                    this.telephoneSelections = Object.assign({}, this.telephoneSelections, {
                        [question.id]: { telephone: '', countryCode: '+971' },
                    });
                }
            }
            if (question.type === 'country' && this.countrySelections[question.id] === undefined) {
                this.countrySelections = Object.assign({}, this.countrySelections, { [question.id]: null });
            }
            if (question.type === 'emirate' && this.emirateSelections[question.id] === undefined) {
                this.emirateSelections = Object.assign({}, this.emirateSelections, { [question.id]: null });
            }
            if (question.type === 'geo_location' && !this.geoSelections[question.id]) {
                this.geoSelections = Object.assign({}, this.geoSelections, {
                    [question.id]: { lat: null, lng: null, address: '', error: '' },
                });
            }
            if (question.type === 'price' && !this.priceSelections[question.id]) {
                this.priceSelections = Object.assign({}, this.priceSelections, {
                    [question.id]: { amount: '', currency: '' },
                });
            }
            if (question.type === 'switch') {
                if (this.switchSelections[question.id] === undefined) {
                    this.switchSelections = Object.assign({}, this.switchSelections, { [question.id]: null });
                }
            }
            if (question.type === 'table') {
                const rows = question.table_rows || 1;
                const columns = question.options ? question.options.length : 2;
                if (!this.tableAnswers[question.id]) {
                    this.tableAnswers[question.id] = [];
                    for (let r = 0; r < rows; r++) {
                        this.tableAnswers[question.id][r] = [];
                        for (let c = 0; c < columns; c++) {
                            this.tableAnswers[question.id][r][c] = '';
                        }
                    }
                    if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                        this.answers[question.id].answer_text = '';
                    }
                }
            }
            if (question.type === 'grid_single_select') {
                if (!this.gridAnswers[question.id]) {
                    this.gridAnswers[question.id] = {};
                    if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                        this.answers[question.id].answer_text = '{}';
                    }
                }
            }
            if (question.type === 'grid_multiple_select') {
                if (!this.gridMultipleAnswers[question.id]) {
                    const rowTitles = this.getGridRowTitles(question);
                    const rowData = {};
                    for (let i = 0; i < rowTitles.length; i++) {
                        rowData[i] = [];
                    }
                    this.gridMultipleAnswers[question.id] = rowData;
                    if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                        this.answers[question.id].answer_text = '{}';
                    }
                }
            }
            if (question.type === 'signature' && !this.signatureDrawing[question.id]) {
                this.signatureDrawing[question.id] = { isDrawing: false, lastX: 0, lastY: 0 };
            }
            if (question.type === 'select' && this.selectSelections[question.id] === undefined) {
                this.selectSelections = Object.assign({}, this.selectSelections, { [question.id]: null });
            }
        },
        getFileUploadAccept(question) {
            const types = question.file_upload_types || ['image'];
            const map = {
                excel: '.xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                pdf: '.pdf,application/pdf',
                image: '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp',
                ppt: '.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
                csv: '.csv,text/csv',
                word: '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                video: '.mp4,.webm,.ogg,.mov,video/mp4,video/webm,video/ogg,video/quicktime',
                audio: '.mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/x-m4a',
            };
            return types.map(t => map[t] || '').filter(Boolean).join(',');
        },
        triggerFileUpload(question) {
            this.currentFileUploadQuestion = question;
            this.$nextTick(() => {
                const input = this.$refs.fileUploadInput;
                if (input) {
                    input.accept = this.getFileUploadAccept(question);
                    input.value = '';
                    input.click();
                }
            });
        },
        async onFileUploadChange(event) {
            const question = this.currentFileUploadQuestion;
            const file = event.target && event.target.files && event.target.files[0];
            if (!question || !file) {
                event.target.value = '';
                this.currentFileUploadQuestion = null;
                return;
            }
            const maxMb = question.file_upload_max_size_mb || 2;
            if (file.size > maxMb * 1024 * 1024) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'File too large',
                    text: `Maximum size is ${maxMb} MB.`,
                });
                event.target.value = '';
                this.currentFileUploadQuestion = null;
                return;
            }
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: UPLOAD_SURVEY_FILE,
                    variables: {
                        survey_id: this.survey.id,
                        question_id: question.id,
                        file,
                    },
                });
                const result = data.uploadSurveyFile;
                if (result.error || !result.path) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Upload failed',
                        text: result.message || 'Please try again.',
                    });
                } else {
                    if (!this.answers[question.id]) {
                        this.answers[question.id] = { question_id: question.id, answer_text: '' };
                    }
                    this.answers[question.id].answer_text = result.path;
                    this.fileUploadNames = { ...this.fileUploadNames, [question.id]: file.name };
                }
            } catch (err) {
                console.error(err);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Upload failed',
                    text: 'Please try again.',
                });
            }
            event.target.value = '';
            this.currentFileUploadQuestion = null;
        },
        clearFileUpload(questionId) {
            if (this.answers[questionId]) {
                this.answers[questionId].answer_text = '';
            }
            const names = { ...this.fileUploadNames };
            delete names[questionId];
            this.fileUploadNames = names;
            const input = this.$refs.fileUploadInput;
            if (input) {
                input.value = '';
            }
        },
        getSignatureCanvas(questionId) {
            const ref = this.$refs[`signatureCanvas_${questionId}`];
            if (Array.isArray(ref) && ref.length > 0) {
                return ref[0];
            }
            return ref;
        },
        getSignatureContext(questionId) {
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas) return null;
            const ctx = canvas.getContext('2d');
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            return ctx;
        },
        getEventCoordinates(event, questionId) {
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas) return { x: 0, y: 0 };
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            
            if (event.touches && event.touches.length > 0) {
                return {
                    x: (event.touches[0].clientX - rect.left) * scaleX,
                    y: (event.touches[0].clientY - rect.top) * scaleY
                };
            }
            return {
                x: (event.clientX - rect.left) * scaleX,
                y: (event.clientY - rect.top) * scaleY
            };
        },
        startSignature(questionId, event) {
            event.preventDefault();
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas) return;
            
            if (!this.signatureDrawing[questionId]) {
                this.signatureDrawing[questionId] = { isDrawing: false, lastX: 0, lastY: 0 };
            }
            
            const coords = this.getEventCoordinates(event, questionId);
            this.signatureDrawing[questionId].isDrawing = true;
            this.signatureDrawing[questionId].lastX = coords.x;
            this.signatureDrawing[questionId].lastY = coords.y;
        },
        drawSignature(questionId, event) {
            event.preventDefault();
            if (!this.signatureDrawing[questionId] || !this.signatureDrawing[questionId].isDrawing) return;
            
            const ctx = this.getSignatureContext(questionId);
            if (!ctx) return;
            
            const coords = this.getEventCoordinates(event, questionId);
            const { lastX, lastY } = this.signatureDrawing[questionId];
            
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            
            this.signatureDrawing[questionId].lastX = coords.x;
            this.signatureDrawing[questionId].lastY = coords.y;
            
            // Save signature to answer_text as base64
            this.saveSignature(questionId);
        },
        stopSignature(questionId) {
            if (this.signatureDrawing[questionId]) {
                this.signatureDrawing[questionId].isDrawing = false;
            }
        },
        saveSignature(questionId) {
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas) return;
            
            try {
                const dataURL = canvas.toDataURL('image/png');
                if (!this.answers[questionId]) {
                    this.answers[questionId] = { question_id: questionId, answer_text: '' };
                }
                this.answers[questionId].answer_text = dataURL;
            } catch (error) {
                console.error('Error saving signature:', error);
            }
        },
        clearSignature(questionId) {
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            if (this.answers[questionId]) {
                this.answers[questionId].answer_text = '';
            }
            
            if (this.signatureDrawing[questionId]) {
                this.signatureDrawing[questionId].isDrawing = false;
            }
        },
        updateSignatureCanvasWidth() {
            this.$nextTick(() => {
                const container = document.querySelector('.container');
                if (container) {
                    const containerWidth = container.clientWidth;
                    this.signatureCanvasWidth = Math.min(600, containerWidth - 64); // 64px for padding
                }
            });
        },
        loadSignature(questionId, dataURL) {
            const canvas = this.getSignatureCanvas(questionId);
            if (!canvas || !dataURL) return;
            
            const img = new Image();
            img.onload = () => {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            };
            img.src = dataURL;
        },
        normalizeDateTimeToSql(value) {
            if (!value) {
                return '';
            }
            const str = value.toString().trim();
            if (!str) {
                return '';
            }
            // Accept both "YYYY-MM-DDTHH:mm" (datetime-local) and "YYYY-MM-DD HH:mm:ss"
            if (str.indexOf('T') !== -1) {
                // datetime-local returns without seconds
                const parts = str.split('T');
                const date = parts[0] || '';
                const time = parts[1] || '';
                if (!date || !time) {
                    return '';
                }
                // time can be HH:mm or HH:mm:ss
                const timeParts = time.split(':');
                const hh = timeParts[0] || '00';
                const mm = timeParts[1] || '00';
                const ss = timeParts[2] || '00';
                return `${date} ${hh}:${mm}:${ss}`;
            }
            // If already space-separated, ensure seconds exist
            if (str.indexOf(' ') !== -1) {
                const parts = str.split(' ');
                const date = parts[0] || '';
                const time = parts[1] || '';
                if (!date || !time) {
                    return '';
                }
                const timeParts = time.split(':');
                if (timeParts.length === 2) {
                    return `${date} ${time}:00`;
                }
                return str;
            }
            return '';
        },
        async loadCountries() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_ALL_COUNTRIES,
                    fetchPolicy: 'cache-first',
                });
                this.countriesList = data && data.countries ? data.countries : [];
            } catch (e) {
                this.countriesList = [];
            }
        },
        onCountrySelected(questionId, country) {
            if (!questionId || !this.answers[questionId]) {
                return;
            }
            const name = country && country.country_name ? country.country_name : '';
            this.answers[questionId].answer_text = name;
        },
        onEmirateSelected(questionId, emirate) {
            if (!questionId || !this.answers[questionId]) {
                return;
            }
            const name = emirate || '';
            this.answers[questionId].answer_text = name;
        },
        fetchCurrentLocation(questionId) {
            if (!questionId) {
                return;
            }
            const existing = this.geoSelections[questionId] || { lat: null, lng: null, address: '', error: '' };
            const updatedGeo = Object.assign({}, this.geoSelections, { [questionId]: Object.assign({}, existing, { error: '' }) });
            this.geoSelections = updatedGeo;

            if (!navigator.geolocation) {
                this.geoSelections = Object.assign({}, this.geoSelections, {
                    [questionId]: Object.assign({}, this.geoSelections[questionId], { error: 'Geolocation is not supported in this browser.' })
                });
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos && pos.coords ? pos.coords.latitude : null;
                    const lng = pos && pos.coords ? pos.coords.longitude : null;
                    const address = lat && lng ? `${lat}, ${lng}` : '';
                    this.geoSelections = Object.assign({}, this.geoSelections, {
                        [questionId]: { lat: lat, lng: lng, address: address, error: '' }
                    });
                    if (this.answers[questionId]) {
                        this.answers[questionId].answer_text = JSON.stringify({ lat: lat, lng: lng });
                    }
                },
                () => {
                    this.geoSelections = Object.assign({}, this.geoSelections, {
                        [questionId]: Object.assign({}, this.geoSelections[questionId], { error: 'Unable to fetch your location. Please allow location access.' })
                    });
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        },
        isValidEmail(value) {
            if (!value) {
                return false;
            }
            const str = value.toString().trim();
            if (!str) {
                return false;
            }
            // Simple and safe email format check
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(str);
        },
        syncTelephoneAnswer(questionId) {
            if (!questionId) {
                return;
            }
            if (!this.answers[questionId]) {
                this.answers[questionId] = {
                    question_id: questionId,
                    answer_text: '',
                };
            }
            if (!this.telephoneSelections[questionId]) {
                this.telephoneSelections = Object.assign({}, this.telephoneSelections, {
                    [questionId]: { telephone: '', countryCode: '+971' }
                });
            }

            const item = this.telephoneSelections[questionId];
            const code = item && item.countryCode ? item.countryCode.toString().trim() : '';
            const tel = item && item.telephone ? item.telephone.toString().trim() : '';
            const combined = `${code}${tel ? ' ' + tel : ''}`.trim();
            this.answers[questionId].answer_text = combined;
        },
        isValidUrl(value) {
            if (!value) {
                return false;
            }
            const raw = value.toString().trim();
            if (!raw) {
                return false;
            }

            // Allow users to enter without protocol (e.g. www.example.com, example.com)
            const hasProtocol = raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0;
            const normalized = hasProtocol ? raw : `https://${raw}`;

            try {
                const url = new URL(normalized);
                // Basic sanity: require host and a dot (avoid single-word hosts)
                if (!url.hostname) {
                    return false;
                }
                if (url.hostname.indexOf('.') === -1) {
                    return false;
                }
                return true;
            } catch (e) {
                return false;
            }
        },
        getFullImageUrl(path) {
            if (!path) return '';
            if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
                return path;
            }
            const baseUrl = process.env.VUE_APP_PUBLIC_URL || '';
            return `${baseUrl}${path}`;
        },
        setRatingHover(questionId, value) {
            this.ratingHover = Object.assign({}, this.ratingHover, { [questionId]: value });
        },
        clearRatingHover(questionId) {
            if (this.ratingHover[questionId]) {
                const updated = Object.assign({}, this.ratingHover);
                delete updated[questionId];
                this.ratingHover = updated;
            }
        },
        getStarFillColor(questionId, ratingValue) {
            const hoverValue = this.ratingHover[questionId];
            if (hoverValue && ratingValue <= hoverValue) {
                return '#b47c2c';
            }
            const answer = this.answers[questionId];
            let selectedValue = 0;
            if (answer && answer.answer_text) {
                selectedValue = parseInt(answer.answer_text, 10);
            }
            return ratingValue <= selectedValue ? '#b47c2c' : '#d9c8ab';
        },
        initializeSelectSelections() {
            const map = {};
            if (this.survey && this.survey.questions) {
                this.survey.questions.forEach(question => {
                    if (question.type === 'select') {
                        map[question.id] = this.getSelectOption(question);
                    }
                });
            }
            this.selectSelections = map;
        },
        getSelectOption(question) {
            if (!question || !question.options || !question.options.length) {
                return null;
            }
            const answer = this.answers[question.id];
            if (!answer || !answer.option_id) {
                return null;
            }
            const selectedId = parseInt(answer.option_id, 10);
            return question.options.find(option => parseInt(option.id, 10) === selectedId) || null;
        },
        onSelectOption(question, option) {
            if (!question || !this.answers[question.id]) {
                return;
            }
            const selectedId = option && option.id !== undefined && option.id !== null
                ? parseInt(option.id, 10)
                : null;
            const updatedSelections = Object.assign({}, this.selectSelections, { [question.id]: option || null });
            this.selectSelections = updatedSelections;
            this.answers[question.id].option_id = selectedId;
            this.onChoiceOptionChange(question);
        },
        onChoiceOptionChange(question) {
            if (!question || !this.answers[question.id]) {
                return;
            }
            if (!shouldShowOtherCommentField(question, this.answers[question.id])) {
                this.answers[question.id].answer_text = '';
            }
        },
        shouldShowOtherCommentField(question, answer) {
            return shouldShowOtherCommentField(question, answer);
        },
        isOtherOptionSelected(question, answer) {
            return isOtherOptionSelected(question, answer);
        },
        sortedOptions(options) {
            if (!options) return [];
            return [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        getTableRows(question) {
            const rows = question.table_rows || 1;
            return Array.from({ length: rows }, (_, i) => i);
        },
        getGridRowTitles(question) {
            if (!question.grid_row_titles || !Array.isArray(question.grid_row_titles)) {
                return [];
            }
            return question.grid_row_titles;
        },
        updateGridAnswer(questionId, rowIndex, columnOptionId) {
            if (!this.gridAnswers[questionId]) {
                this.gridAnswers[questionId] = {};
            }
            this.gridAnswers[questionId][rowIndex] = columnOptionId ? parseInt(columnOptionId) : null;
            
            // Store as JSON in answer_text: { "0": columnOptionId1, "1": columnOptionId2, ... }
            if (this.answers[questionId]) {
                const gridData = this.gridAnswers[questionId];
                this.answers[questionId].answer_text = JSON.stringify(gridData);
            }
        },
        isGridMultipleOptionSelected(questionId, rowIndex, optionId) {
            const row = this.gridMultipleAnswers[questionId] && this.gridMultipleAnswers[questionId][rowIndex];
            if (!Array.isArray(row)) return false;
            const idStr = String(optionId);
            const idNum = parseInt(optionId, 10);
            return row.some(id => String(id) === idStr || parseInt(id, 10) === idNum);
        },
        toggleGridMultipleOption(questionId, rowIndex, optionId) {
            if (!this.gridMultipleAnswers[questionId]) {
                this.gridMultipleAnswers[questionId] = {};
            }
            let row = this.gridMultipleAnswers[questionId][rowIndex];
            if (!Array.isArray(row)) {
                row = [];
            }
            const idStr = String(optionId);
            const idNum = parseInt(optionId, 10);
            const isSelected = row.some(id => String(id) === idStr || parseInt(id, 10) === idNum);
            let newRow;
            if (isSelected) {
                newRow = row.filter(id => String(id) !== idStr && parseInt(id, 10) !== idNum);
            } else {
                newRow = row.slice();
                newRow.push(idStr);
            }
            this.gridMultipleAnswers[questionId] = {
                ...this.gridMultipleAnswers[questionId],
                [rowIndex]: newRow,
            };
            this.updateGridMultipleAnswer(questionId);
        },
        updateGridMultipleAnswer(questionId) {
            // Store as JSON in answer_text: { "0": [columnOptionId1, columnOptionId2], "1": [columnOptionId3], ... }
            if (this.answers[questionId] && this.gridMultipleAnswers[questionId]) {
                const gridData = {};
                Object.keys(this.gridMultipleAnswers[questionId]).forEach(key => {
                    const optionIds = this.gridMultipleAnswers[questionId][key];
                    if (Array.isArray(optionIds) && optionIds.length > 0) {
                        gridData[key] = optionIds.map(id => parseInt(id, 10));
                    }
                });
                this.answers[questionId].answer_text = JSON.stringify(gridData);
            }
        },
        updateTableAnswer(questionId, rowIndex, colIndex, value) {
            if (!this.tableAnswers[questionId] || !this.tableAnswers[questionId][rowIndex]) {
                return;
            }
            this.tableAnswers[questionId][rowIndex][colIndex] = value;
            // Update the answer_text with JSON representation of the table
            if (this.answers[questionId]) {
                const tableData = this.tableAnswers[questionId];
                this.answers[questionId].answer_text = JSON.stringify(tableData);
            }
        },
        getProgressStorageKey() {
            if (!this.survey || !this.survey.id) {
                return null;
            }
            return `survey_progress_token_${this.survey.id}`;
        },
        getStoredProgressToken() {
            const key = this.getProgressStorageKey();
            if (!key || typeof window === 'undefined' || !window.localStorage) {
                return null;
            }
            return window.localStorage.getItem(key);
        },
        setStoredProgressToken(token) {
            const key = this.getProgressStorageKey();
            if (!key || !token || typeof window === 'undefined' || !window.localStorage) {
                return;
            }
            window.localStorage.setItem(key, token);
        },
        clearStoredProgressToken() {
            const key = this.getProgressStorageKey();
            if (!key || typeof window === 'undefined' || !window.localStorage) {
                return;
            }
            window.localStorage.removeItem(key);
        },
        scheduleProgressSave() {
            if (this.progressSaveTimer) {
                clearTimeout(this.progressSaveTimer);
            }
            this.progressSaveTimer = setTimeout(() => {
                this.progressSaveTimer = null;
                this.saveProgressNow();
            }, 2000);
        },
        syncCompositeAnswersBeforeSave() {
            if (!this.survey || !this.survey.questions) {
                return;
            }
            this.sortedQuestions.forEach((question) => {
                if (this.isStructuralQuestionType(question.type)) {
                    return;
                }
                if (question.type === 'price') {
                    const item = this.priceSelections && this.priceSelections[question.id]
                        ? this.priceSelections[question.id]
                        : null;
                    const amount = item && item.amount !== undefined && item.amount !== null
                        ? item.amount.toString().trim()
                        : '';
                    const currency = item && item.currency ? item.currency.toString().trim() : '';
                    if (this.answers[question.id] && (amount !== '' || currency !== '')) {
                        this.answers[question.id].answer_text = JSON.stringify({ amount: amount, currency: currency });
                    }
                }
                if (question.type === 'table' && this.tableAnswers[question.id] && this.answers[question.id]) {
                    this.answers[question.id].answer_text = JSON.stringify(this.tableAnswers[question.id]);
                }
                if (question.type === 'grid_single_select' && this.gridAnswers[question.id] && this.answers[question.id]) {
                    this.answers[question.id].answer_text = JSON.stringify(this.gridAnswers[question.id]);
                }
                if (question.type === 'grid_multiple_select' && this.gridMultipleAnswers[question.id] && this.answers[question.id]) {
                    this.answers[question.id].answer_text = JSON.stringify(this.gridMultipleAnswers[question.id]);
                }
            });
        },
        collectAnswersArray() {
            this.syncCompositeAnswersBeforeSave();
            return Object.values(this.answers).map((answer) => {
                const result = {
                    question_id: parseInt(answer.question_id, 10),
                };
                const question = this.sortedQuestions.find((q) => String(q.id) === String(answer.question_id));

                if (question && question.type === 'switch') {
                    const switchValue = this.switchSelections[question.id];
                    if (switchValue === null || switchValue === undefined) {
                        result.answer_text = '';
                    } else {
                        result.answer_text = String(switchValue);
                    }
                } else if (question && ['radio', 'checkbox', 'select'].includes(question.type)) {
                    if (shouldShowOtherCommentField(question, answer) && answer.answer_text) {
                        result.answer_text = answer.answer_text.toString().trim();
                    }
                } else if (answer.answer_text !== undefined) {
                    result.answer_text = answer.answer_text.toString();
                }

                if (answer.option_id !== undefined && answer.option_id !== null) {
                    result.option_id = parseInt(answer.option_id, 10);
                }
                if (answer.option_ids !== undefined && Array.isArray(answer.option_ids) && answer.option_ids.length > 0) {
                    result.option_ids = answer.option_ids.map((id) => parseInt(id, 10));
                }

                return result;
            });
        },
        async initializeSurveyProgress() {
            if (!this.survey || !this.survey.id) {
                return;
            }

            const storedToken = this.getStoredProgressToken();
            if (storedToken) {
                const restored = await this.loadPartialProgress(storedToken);
                if (restored) {
                    return;
                }
            }

            await this.saveProgressNow();
        },
        async loadPartialProgress(token) {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_RESPONSE_BY_TOKEN,
                    variables: {
                        survey_id: String(this.survey.id),
                        token: token,
                    },
                    fetchPolicy: 'network-only',
                });

                const result = data && data.surveyResponseByToken ? data.surveyResponseByToken : null;
                if (!result || result.error || !result.data) {
                    this.clearStoredProgressToken();
                    return false;
                }

                if (result.data.is_submitted) {
                    this.clearStoredProgressToken();
                    return false;
                }

                this.restoringProgress = true;
                this.responseToken = result.data.token || token;
                this.setStoredProgressToken(this.responseToken);
                await this.restoreSavedAnswers(result.data.answers || []);
                this.restoringProgress = false;
                return true;
            } catch (error) {
                console.error(error);
                this.clearStoredProgressToken();
                this.restoringProgress = false;
                return false;
            }
        },
        async restoreSavedAnswers(savedAnswers) {
            if (!savedAnswers || !Array.isArray(savedAnswers)) {
                return;
            }

            savedAnswers.forEach((savedAnswer) => {
                const questionId = savedAnswer.question_id;
                const question = this.survey.questions.find((q) => String(q.id) === String(questionId));
                if (!question || this.isStructuralQuestionType(question.type)) {
                    return;
                }

                this.ensureAnswersForVisibleQuestions();

                if (question.type === 'checkbox') {
                    this.answers[questionId] = {
                        question_id: questionId,
                        option_ids: savedAnswer.option_ids
                            ? savedAnswer.option_ids.map((id) => parseInt(id, 10))
                            : [],
                        answer_text: savedAnswer.answer_text || '',
                    };
                } else if (['radio', 'select'].includes(question.type)) {
                    this.answers[questionId] = {
                        question_id: questionId,
                        option_id: savedAnswer.option_id ? parseInt(savedAnswer.option_id, 10) : null,
                        answer_text: savedAnswer.answer_text || '',
                    };
                } else {
                    this.answers[questionId] = {
                        question_id: questionId,
                        answer_text: savedAnswer.answer_text || '',
                    };
                }

                if (question.type === 'telephone' && savedAnswer.answer_text) {
                    const parts = savedAnswer.answer_text.toString().trim().split(' ');
                    const countryCode = parts.length > 1 ? parts[0] : '+971';
                    const telephone = parts.length > 1 ? parts.slice(1).join(' ') : savedAnswer.answer_text;
                    this.telephoneSelections = Object.assign({}, this.telephoneSelections, {
                        [questionId]: {
                            telephone: telephone,
                            countryCode: countryCode,
                        },
                    });
                }

                if (question.type === 'country' && savedAnswer.answer_text) {
                    const country = this.countriesList.find((c) => c.country_name === savedAnswer.answer_text);
                    if (country) {
                        this.countrySelections = Object.assign({}, this.countrySelections, { [questionId]: country });
                    }
                }

                if (question.type === 'emirate' && savedAnswer.answer_text) {
                    this.emirateSelections = Object.assign({}, this.emirateSelections, {
                        [questionId]: savedAnswer.answer_text,
                    });
                }

                if (question.type === 'geo_location' && savedAnswer.answer_text) {
                    try {
                        const parsed = JSON.parse(savedAnswer.answer_text);
                        this.geoSelections = Object.assign({}, this.geoSelections, {
                            [questionId]: {
                                lat: parsed.lat,
                                lng: parsed.lng,
                                address: parsed.lat && parsed.lng ? `${parsed.lat}, ${parsed.lng}` : '',
                                error: '',
                            },
                        });
                    } catch (e) {
                        // ignore invalid geo payload
                    }
                }

                if (question.type === 'price' && savedAnswer.answer_text) {
                    try {
                        const parsed = JSON.parse(savedAnswer.answer_text);
                        this.priceSelections = Object.assign({}, this.priceSelections, {
                            [questionId]: {
                                amount: parsed.amount || '',
                                currency: parsed.currency || '',
                            },
                        });
                    } catch (e) {
                        // ignore invalid price payload
                    }
                }

                if (question.type === 'switch' && savedAnswer.answer_text !== null && savedAnswer.answer_text !== '') {
                    const switchValue = savedAnswer.answer_text === '0' ? 0 : savedAnswer.answer_text === '1' ? 1 : null;
                    this.switchSelections = Object.assign({}, this.switchSelections, { [questionId]: switchValue });
                }
            });

            this.initializeSelectSelections();

            this.$nextTick(() => {
                savedAnswers.forEach((savedAnswer) => {
                    const questionId = savedAnswer.question_id;
                    const question = this.survey.questions.find((q) => String(q.id) === String(questionId));
                    if (!question) {
                        return;
                    }

                    if (question.type === 'grid_single_select' && savedAnswer.answer_text) {
                        try {
                            const savedData = JSON.parse(savedAnswer.answer_text);
                            if (savedData && typeof savedData === 'object') {
                                this.gridAnswers[questionId] = savedData;
                            }
                        } catch (e) {
                            console.error('Error parsing grid answer:', e);
                        }
                    }

                    if (question.type === 'grid_multiple_select' && savedAnswer.answer_text) {
                        try {
                            const savedData = JSON.parse(savedAnswer.answer_text);
                            if (savedData && typeof savedData === 'object') {
                                const rowData = {};
                                Object.keys(savedData).forEach((rowIndex) => {
                                    const optionIds = savedData[rowIndex];
                                    rowData[rowIndex] = Array.isArray(optionIds)
                                        ? optionIds.map((id) => String(id))
                                        : [];
                                });
                                this.gridMultipleAnswers[questionId] = rowData;
                            }
                        } catch (e) {
                            console.error('Error parsing grid multiple answer:', e);
                        }
                    }

                    if (question.type === 'table' && savedAnswer.answer_text) {
                        try {
                            const savedData = JSON.parse(savedAnswer.answer_text);
                            if (savedData && Array.isArray(savedData)) {
                                this.tableAnswers[questionId] = savedData;
                            }
                        } catch (e) {
                            console.error('Error parsing table answer:', e);
                        }
                    }

                    if (question.type === 'signature' && savedAnswer.answer_text) {
                        this.loadSignature(questionId, savedAnswer.answer_text);
                    }
                });
            });
        },
        async saveProgressNow() {
            if (!this.survey || !this.survey.id || this.submitted || this.restoringProgress || this.progressSaving) {
                return;
            }

            this.progressSaving = true;

            try {
                const input = {
                    survey_id: parseInt(this.survey.id, 10),
                    answers: this.collectAnswersArray(),
                };

                if (this.responseToken) {
                    input.token = this.responseToken;
                }

                const { data } = await this.$apollo.mutate({
                    mutation: SAVE_SURVEY_PROGRESS,
                    variables: { input: input },
                });

                const result = data && data.saveSurveyProgress ? data.saveSurveyProgress : null;
                if (result && !result.error && result.data && result.data.token) {
                    this.responseToken = result.data.token;
                    this.setStoredProgressToken(this.responseToken);
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.progressSaving = false;
            }
        },
        async loadSurvey() {
            this.loading = true;
            this.error = null;

            try {
                const slug = this.$route.params.slug;
                await this.loadCountries();
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_BY_SLUG,
                    variables: { slug: slug, forParticipation: true },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.surveyBySlug) {
                    this.error = 'Failed to load survey. Please try again later.';
                    return;
                }

                const result = data.surveyBySlug;

                if (result.error) {
                    this.error = result.message || 'Failed to load survey.';
                    return;
                }

                if (!result.data) {
                    this.error = 'Survey not found.';
                    return;
                }

                this.survey = result.data;
                this.currentPageIndex = 0;

                if (!this.survey.is_active) {
                    this.error = 'This survey is currently inactive.';
                    return;
                }

                // Initialize answers object
                const telSelections = {};
                const countrySel = {};
                const emirateSel = {};
                const geoSel = {};
                const priceSel = {};
                const switchSel = {};
                const visibleQuestions = this.survey.questions.filter(q => !q.hidden_by_default);
                visibleQuestions.forEach(question => {
                    if (this.isStructuralQuestionType(question.type)) return;
                    if (question.type === 'checkbox') {
                        this.answers[question.id] = {
                            question_id: question.id,
                            option_ids: [],
                            answer_text: '',
                        };
                    } else if (['radio', 'select'].includes(question.type)) {
                        this.answers[question.id] = {
                            question_id: question.id,
                            option_id: null,
                            answer_text: '',
                        };
                    } else {
                        // text, textarea, rating, emoji_rating all use answer_text
                        this.answers[question.id] = {
                            question_id: question.id,
                            answer_text: '',
                        };
                    }

                    if (question.type === 'telephone') {
                        telSelections[question.id] = {
                            telephone: '',
                            countryCode: '+971',
                        };
                    }
                    if (question.type === 'country') {
                        countrySel[question.id] = null;
                    }
                    if (question.type === 'emirate') {
                        emirateSel[question.id] = null;
                    }
                    if (question.type === 'geo_location') {
                        geoSel[question.id] = { lat: null, lng: null, address: '', error: '' };
                    }
                    if (question.type === 'price') {
                        priceSel[question.id] = { amount: '', currency: '' };
                    }
                    if (question.type === 'switch') {
                        switchSel[question.id] = null;
                        // Center (null) is represented as empty string in answer_text
                        if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                            this.answers[question.id].answer_text = '';
                        }
                    }
                    if (question.type === 'table') {
                        // Initialize table answers structure
                        const rows = question.table_rows || 1;
                        const columns = question.options ? question.options.length : 2;
                        this.tableAnswers[question.id] = [];
                        for (let r = 0; r < rows; r++) {
                            this.tableAnswers[question.id][r] = [];
                            for (let c = 0; c < columns; c++) {
                                this.tableAnswers[question.id][r][c] = '';
                            }
                        }
                        // Initialize answer_text as empty, will be populated on submit
                        if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                            this.answers[question.id].answer_text = '';
                        }
                    }
                    if (question.type === 'grid_single_select') {
                        // Initialize grid answers structure: { rowIndex: columnOptionId, ... }
                        this.gridAnswers[question.id] = {};
                        // Initialize answer_text as empty JSON object
                        if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                            this.answers[question.id].answer_text = '{}';
                        }
                    }
                    if (question.type === 'grid_multiple_select') {
                        // Initialize grid multiple answers structure: { rowIndex: [columnOptionId1, columnOptionId2, ...], ... }
                        // Build full object so each row has its own array (reactive without $set)
                        const rowTitles = this.getGridRowTitles(question);
                        const rowData = {};
                        for (let i = 0; i < rowTitles.length; i++) {
                            rowData[i] = [];
                        }
                        this.gridMultipleAnswers[question.id] = rowData;
                        // Initialize answer_text as empty JSON object
                        if (this.answers[question.id] && 'answer_text' in this.answers[question.id]) {
                            this.answers[question.id].answer_text = '{}';
                        }
                    }
                    if (question.type === 'signature') {
                        // Initialize signature drawing state
                        this.signatureDrawing[question.id] = { isDrawing: false, lastX: 0, lastY: 0 };
                    }
                });

                // Load saved grid answers after initialization
                this.$nextTick(() => {
                    visibleQuestions.forEach(question => {
                        if (question.type === 'grid_single_select' && this.answers[question.id] && this.answers[question.id].answer_text) {
                            try {
                                const savedData = JSON.parse(this.answers[question.id].answer_text);
                                if (savedData && typeof savedData === 'object') {
                                    this.gridAnswers[question.id] = savedData;
                                }
                            } catch (e) {
                                console.error('Error parsing grid answer:', e);
                            }
                        }
                        if (question.type === 'grid_multiple_select' && this.answers[question.id] && this.answers[question.id].answer_text) {
                            try {
                                const savedData = JSON.parse(this.answers[question.id].answer_text);
                                if (savedData && typeof savedData === 'object') {
                                    // Convert saved data format { rowIndex: [optionIds] } to match our structure (build full object for reactivity)
                                    const rowData = {};
                                    Object.keys(savedData).forEach(rowIndex => {
                                        const optionIds = savedData[rowIndex];
                                        rowData[rowIndex] = Array.isArray(optionIds) ? optionIds.map(id => String(id)) : [];
                                    });
                                    this.gridMultipleAnswers[question.id] = rowData;
                                }
                            } catch (e) {
                                console.error('Error parsing grid multiple answer:', e);
                            }
                        }
                    });
                });

                // Load saved signatures after initialization
                this.$nextTick(() => {
                    visibleQuestions.forEach(question => {
                        if (question.type === 'signature' && this.answers[question.id] && this.answers[question.id].answer_text) {
                            this.loadSignature(question.id, this.answers[question.id].answer_text);
                        }
                    });
                });

                this.telephoneSelections = telSelections;
                this.countrySelections = countrySel;
                this.emirateSelections = emirateSel;
                this.geoSelections = geoSel;
                this.priceSelections = priceSel;
                this.switchSelections = switchSel;
                this.initializeSelectSelections();
                await this.initializeSurveyProgress();
            } catch (error) {
                console.error(error);
                let errorMessage = 'Failed to load survey. Please try again later.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.error = errorMessage;
            } finally {
                this.loading = false;
            }
        },
        async submitResponse() {
            // Normalize + validate composite question types BEFORE required checks
            for (const question of this.sortedQuestions) {
                if (this.isStructuralQuestionType(question.type)) continue;
                if (question.type === 'price') {
                    const item = this.priceSelections && this.priceSelections[question.id] ? this.priceSelections[question.id] : null;
                    const amount = item && item.amount !== undefined && item.amount !== null ? item.amount.toString().trim() : '';
                    const currency = item && item.currency ? item.currency.toString().trim() : '';
                    const hasAny = amount !== '' || currency !== '';

                    if ((question.is_required || hasAny) && (amount === '' || currency === '')) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please enter amount and currency for: ${question.question_text}`,
                        });
                        return;
                    }

                    // Store as JSON string in answer_text (even for required, once valid)
                    if ((question.is_required || hasAny) && this.answers[question.id]) {
                        this.answers[question.id].answer_text = JSON.stringify({ amount: amount, currency: currency });
                    }
                }
                if (question.type === 'date_time') {
                    const answer = this.answers[question.id];
                    const raw = answer && answer.answer_text !== undefined && answer.answer_text !== null
                        ? answer.answer_text.toString().trim()
                        : '';
                    const hasAny = raw !== '';
                    if (question.is_required && !hasAny) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please select date and time for: ${question.question_text}`,
                        });
                        return;
                    }
                    if (hasAny) {
                        const normalized = this.normalizeDateTimeToSql(raw);
                        if (!normalized) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Invalid date/time format for: ${question.question_text}`,
                            });
                            return;
                        }
                        this.answers[question.id].answer_text = normalized;
                    }
                }
            }

            // Validate required questions
            for (const question of this.sortedQuestions) {
                if (this.isStructuralQuestionType(question.type)) continue;
                if (question.is_required) {
                    const answer = this.answers[question.id];
                    if (question.type === 'checkbox') {
                        if (!answer.option_ids || answer.option_ids.length === 0) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please answer: ${question.question_text}`,
                            });
                            return;
                        }
                        if (shouldShowOtherCommentField(question, answer)
                            && (!answer.answer_text || !answer.answer_text.toString().trim())) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please specify your Other response for: ${question.question_text}`,
                            });
                            return;
                        }
                    } else if (['radio', 'select'].includes(question.type)) {
                        if (!answer.option_id) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please answer: ${question.question_text}`,
                            });
                            return;
                        }
                        if (shouldShowOtherCommentField(question, answer)
                            && (!answer.answer_text || !answer.answer_text.toString().trim())) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please specify your Other response for: ${question.question_text}`,
                            });
                            return;
                        }
                    } else if (question.type === 'table') {
                        // Check if at least one cell is filled
                        const tableData = this.tableAnswers[question.id];
                        if (!tableData || !Array.isArray(tableData)) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please answer: ${question.question_text}`,
                            });
                            return;
                        }
                        const hasAnswer = tableData.some(row =>
                            Array.isArray(row) && row.some(cell => cell && cell.toString().trim() !== '')
                        );
                        if (!hasAnswer) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please fill at least one cell in: ${question.question_text}`,
                            });
                            return;
                        }
                    } else if (question.type === 'grid_single_select') {
                        const gridData = this.gridAnswers[question.id];
                        if (!gridData || typeof gridData !== 'object') {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please answer: ${question.question_text}`,
                            });
                            return;
                        }
                        // Check if at least one row has a selection
                        const hasSelection = Object.values(gridData).some(val => val !== null && val !== undefined);
                        if (!hasSelection) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please select at least one option for each row in: ${question.question_text}`,
                            });
                            return;
                        }
                    } else if (question.type === 'grid_multiple_select') {
                        const gridData = this.gridMultipleAnswers[question.id];
                        if (!gridData || typeof gridData !== 'object') {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please answer: ${question.question_text}`,
                            });
                            return;
                        }
                        // Check if at least one row has at least one selection
                        const hasSelection = Object.values(gridData).some(val => Array.isArray(val) && val.length > 0);
                        if (!hasSelection) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please select at least one option in: ${question.question_text}`,
                            });
                            return;
                        }
                    } else if (question.type === 'switch') {
                        // Switch questions: value can be null (center), 0 (left), or 1 (right)
                        // For required questions, must select left (0) or right (1), not center (null)
                        const switchValue = this.switchSelections[question.id];
                        if (switchValue === null || switchValue === undefined) {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Validation Error',
                                text: `Please select an option for: ${question.question_text}`,
                            });
                            return;
                        }
                        // Ensure answer_text is set to "0" or "1" (not empty string)
                        if (this.answers[question.id]) {
                            this.answers[question.id].answer_text = String(switchValue);
                        }
                    } else {
                        // text, textarea, rating, emoji_rating all use answer_text
                        // For rating and emoji_rating questions, answer_text is a number/string
                        // For text/textarea questions, answer_text is a string
                        if (question.type === 'rating' || question.type === 'emoji_rating') {
                            if (answer.answer_text === null || answer.answer_text === undefined || answer.answer_text === '') {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: 'Validation Error',
                                    text: `Please answer: ${question.question_text}`,
                                });
                                return;
                            }
                        } else {
                            // For text and textarea, check if string is empty after trimming
                            if (!answer.answer_text || (typeof answer.answer_text === 'string' && answer.answer_text.trim() === '')) {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: 'Validation Error',
                                    text: `Please answer: ${question.question_text}`,
                                });
                                return;
                            }
                        }
                    }
                }
            }

            // Extra validation for composite question types
            for (const question of this.sortedQuestions) {
                if (question.type === 'price') {
                    // Already validated/serialized above
                }

                if (question.type === 'country') {
                    const selected = this.countrySelections[question.id];
                    if (question.is_required && !selected) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please select a country for: ${question.question_text}`,
                        });
                        return;
                    }
                }

                if (question.type === 'emirate') {
                    const selected = this.emirateSelections[question.id];
                    if (question.is_required && !selected) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please select an emirate for: ${question.question_text}`,
                        });
                        return;
                    }
                }

                if (question.type === 'geo_location') {
                    const geo = this.geoSelections[question.id];
                    const hasGeo = geo && geo.lat && geo.lng;
                    if (question.is_required && !hasGeo) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please fetch your location for: ${question.question_text}`,
                        });
                        return;
                    }
                }
            }

            // Validate website URLs if provided (even if not required)
            for (const question of this.sortedQuestions) {
                if (question.type === 'website') {
                    const answer = this.answers[question.id];
                    const value = answer && answer.answer_text !== undefined && answer.answer_text !== null
                        ? answer.answer_text.toString().trim()
                        : '';

                    if (value !== '' && !this.isValidUrl(value)) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please enter a valid website URL for: ${question.question_text}`,
                        });
                        return;
                    }
                }
            }

            // Validate email if provided (even if not required)
            for (const question of this.sortedQuestions) {
                if (question.type === 'email') {
                    const answer = this.answers[question.id];
                    const value = answer && answer.answer_text !== undefined && answer.answer_text !== null
                        ? answer.answer_text.toString().trim()
                        : '';

                    if (value !== '' && !this.isValidEmail(value)) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Validation Error',
                            text: `Please enter a valid email address for: ${question.question_text}`,
                        });
                        return;
                    }
                }
            }

            // Serialize table answers before submission
            for (const question of this.sortedQuestions) {
                if (question.type === 'table' && this.tableAnswers[question.id]) {
                    const tableData = this.tableAnswers[question.id];
                    if (this.answers[question.id]) {
                        this.answers[question.id].answer_text = JSON.stringify(tableData);
                    }
                }
                if (question.type === 'grid_single_select' && this.gridAnswers[question.id]) {
                    const gridData = this.gridAnswers[question.id];
                    if (this.answers[question.id]) {
                        this.answers[question.id].answer_text = JSON.stringify(gridData);
                    }
                }
                if (question.type === 'grid_multiple_select' && this.gridMultipleAnswers[question.id]) {
                    const gridData = {};
                    Object.keys(this.gridMultipleAnswers[question.id]).forEach(key => {
                        const optionIds = this.gridMultipleAnswers[question.id][key];
                        if (Array.isArray(optionIds) && optionIds.length > 0) {
                            gridData[key] = optionIds.map(id => parseInt(id));
                        }
                    });
                    if (this.answers[question.id]) {
                        this.answers[question.id].answer_text = JSON.stringify(gridData);
                    }
                }
                if (question.type === 'switch') {
                    // Ensure switch answer_text is properly set (0 or 1, not empty string)
                    const switchValue = this.switchSelections[question.id];
                    if (this.answers[question.id]) {
                        // For switch questions, answer_text must be "0" or "1" (never empty)
                        // If switchValue is null, it means user hasn't selected (should be caught by validation for required)
                        // But to ensure backend doesn't reject, set to "0" as default
                        if (switchValue === null || switchValue === undefined) {
                            // Default to "0" to satisfy backend validation (even though validation should have caught this)
                            this.answers[question.id].answer_text = '0';
                        } else {
                            this.answers[question.id].answer_text = String(switchValue);
                        }
                    }
                }
            }

            this.submitting = true;

            try {
                const answersArray = this.collectAnswersArray().map((answer) => {
                    const question = this.sortedQuestions.find((q) => String(q.id) === String(answer.question_id));
                    if (question && question.type === 'switch') {
                        const switchValue = this.switchSelections[question.id];
                        if (switchValue === null || switchValue === undefined) {
                            answer.answer_text = '0';
                        } else {
                            answer.answer_text = String(switchValue);
                        }
                    }
                    return answer;
                });

                const input = {
                    survey_id: parseInt(this.survey.id, 10),
                    answers: answersArray,
                };

                if (this.responseToken) {
                    input.token = this.responseToken;
                }

                // Add contact information for anonymous users on public surveys
                if (this.showContactInformationForm) {
                    const name = this.contactInformation.name ? this.contactInformation.name.trim() : '';
                    const email = this.contactInformation.email ? this.contactInformation.email.trim() : '';
                    const telephone = this.contactInformation.telephone ? this.contactInformation.telephone.trim() : '';
                    const hasContactInfo = name !== '' || email !== '' || telephone !== '';

                    if (hasContactInfo) {
                        if (email) {
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(email)) {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: 'Validation Error',
                                    text: 'Please enter a valid email address.',
                                });
                                this.submitting = false;
                                return;
                            }
                        }

                        const contactInfo = {};
                        if (name) {
                            contactInfo.name = name;
                        }
                        if (email) {
                            contactInfo.email = email;
                        }
                        if (telephone) {
                            contactInfo.telephone = telephone;
                            contactInfo.country_code = this.contactInformation.countryCode || '+971';
                        }

                        if (Object.keys(contactInfo).length > 0) {
                            input.contact_information = contactInfo;
                        }
                    }
                }

                const { data } = await this.$apollo.mutate({
                    mutation: SUBMIT_SURVEY_RESPONSE,
                    variables: {
                        input: input,
                    },
                });

                if (data.submitSurveyResponse.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.submitSurveyResponse.message,
                    });
                } else {
                    this.submitted = true;
                    this.clearStoredProgressToken();
                    this.responseToken = null;
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Response submitted successfully!',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });

                    // Scroll to top when survey is submitted (similar to route navigation)
                    this.$nextTick(() => {
                        setTimeout(() => {
                            const layout = this.$route.meta.layout;
                            if (layout === 'dashboard') {
                                const scrollContainer = document.getElementById('dashboard-scroll-container');
                                if (scrollContainer) {
                                    scrollContainer.scrollTo({
                                        top: 0,
                                        behavior: 'smooth'
                                    });
                                }
                            } else {
                                // For default layout, check for default-scroll-container
                                const scrollContainer = document.getElementById('default-scroll-container');
                                if (scrollContainer) {
                                    scrollContainer.scrollTo({
                                        top: 0,
                                        behavior: 'smooth'
                                    });
                                } else {
                                    // Fallback to window scroll if container not found
                                    window.scrollTo({
                                        top: 0,
                                        behavior: 'smooth'
                                    });
                                }
                            }
                        }, 100);
                    });
                }
            } catch (error) {
                console.error(error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to submit response. Please try again.',
                });
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
/* Rich Text Description Styles */
.survey-description {
    :deep(p) {
        margin-bottom: 0.25rem;
    }

    :deep(p:last-child) {
        margin-bottom: 0;
    }

    :deep(ul),
    :deep(ol) {
        margin-left: 1.5rem;
        margin-bottom: 0.25rem;
    }

    :deep(li) {
        margin-bottom: 0.25rem;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
    }

    :deep(a) {
        color: inherit;
        text-decoration: underline;
    }

    :deep(strong) {
        font-weight: 600;
    }

    :deep(em) {
        font-style: italic;
    }
}

/* Input Styles */
.survey-input,
.survey-textarea,
.survey-select {
    @apply w-full px-4 py-3 rounded-lg;
    @apply text-gray-900 placeholder-gray-400;
    @apply transition-all duration-200;
    border: 1px solid #b47c2c;
    background-color: #fff;
    @apply hover:border-gray-400;
}

.survey-input:focus,
.survey-textarea:focus,
.survey-select:focus {
    outline: none;
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

.survey-input:focus-visible,
.survey-textarea:focus-visible,
.survey-select:focus-visible {
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

.survey-textarea {
    @apply resize-none;
    min-height: 120px;
}

.survey-signature-container {
    border: 1px solid #b47c2c;
    @apply transition-all duration-200;
    @apply hover:border-gray-400;
}

.survey-signature-container:focus-within,
.survey-signature-container.survey-signature-active {
    outline: none;
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

.survey-select {
    @apply cursor-pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
    background-position: right 0.75rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;
    appearance: none;
}

:deep(.multiselect) {
    border-radius: 0.5rem;
}

:deep(.multiselect__tags) {
    border-radius: 0.5rem;
    border: 1px solid #b47c2c;
    padding: 0.5rem 0.75rem;
    min-height: 3rem;
}

:deep(.multiselect--active .multiselect__tags),
:deep(.multiselect:hover .multiselect__tags) {
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

:deep(.multiselect__option--highlight) {
    background-color: #b47c2c;
    color: #fff;
}

:deep(.multiselect__option--highlight:after) {
    background-color: #b47c2c;
}

:deep(.multiselect__content-wrapper) {
    border: 1px solid #b47c2c;
    border-top: none;
    border-radius: 0 0 0.5rem 0.5rem;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}


/* Radio Button Styles */
.survey-radio-option {
    @apply flex items-center justify-between p-2;
    @apply cursor-pointer transition-all duration-200;
    background-color: transparent;
    box-shadow: none;
}

.survey-radio-option:hover {
    background-color: transparent;
    box-shadow: none;
}

.survey-radio-option:has(.survey-radio-input:focus-visible) {
    box-shadow: none;
}

.survey-radio-option:has(.survey-radio-input:checked) {
    background-color: transparent;
    box-shadow: none;
}

.survey-radio-input {
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 9999px;
    border: 2px solid #b47c2c;
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;
    background-color: #fff;
}

.survey-radio-input::after {
    content: '';
    position: absolute;
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 9999px;
    background-color: #b47c2c;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.2s ease;
}

.survey-radio-input:checked::after {
    transform: translate(-50%, -50%) scale(1);
}

.survey-radio-label {
    @apply ml-3 text-gray-800 font-medium cursor-pointer;
    @apply flex-1;
}

/* Checkbox Styles */
.survey-checkbox-option {
    @apply flex items-center p-4 rounded-lg border-2 border-gray-200;
    @apply cursor-pointer transition-all duration-200;
    @apply hover:border-slate-300 hover:bg-slate-50;
}

.survey-checkbox-input:checked~.survey-checkbox-label,
.survey-checkbox-option:has(.survey-checkbox-input:checked) {
    @apply border-slate-600 bg-slate-50;
}

.survey-checkbox-input {
    @apply w-5 h-5 text-slate-600 border-gray-300 rounded;
    @apply focus:ring-slate-500 focus:ring-2;
    @apply cursor-pointer;
}

/* Grid Multiple Select: checkboxes match radio color (gold/brown), no outline when selected */
.survey-grid-multiple-wrap .survey-checkbox-input {
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 0.25rem;
    border: 2px solid #b47c2c;
    background-color: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    box-shadow: none;
}
.survey-grid-multiple-wrap .survey-checkbox-input:focus,
.survey-grid-multiple-wrap .survey-checkbox-input:focus-visible {
    outline: none;
    box-shadow: none;
}
/* Grid tables: gold/brown border color (#b47c2c) */
.survey-grid-table,
.survey-grid-table th,
.survey-grid-table td,
.survey-grid-table tr {
    border-color: #b47c2c;
}

.survey-grid-multiple-wrap .survey-checkbox-input:checked {
    background-color: #b47c2c;
    border-color: #b47c2c;
    outline: none;
    box-shadow: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
    background-size: 0.9em 0.9em;
    background-position: center;
    background-repeat: no-repeat;
}

.survey-checkbox-label {
    @apply ml-3 text-gray-700 font-medium cursor-pointer;
    @apply flex-1;
}

/* Rating Styles */
.survey-rating-option {
    @apply flex flex-col items-center justify-center;
    @apply cursor-pointer transition-all duration-200;
}

.survey-rating-input {
    @apply sr-only;
}

.survey-rating-label {
    @apply flex flex-col items-center space-y-1;
    @apply text-gray-700 font-semibold;
}

.rating-star {
    width: 2rem;
    height: 2rem;
    transition: fill 0.2s ease;
}

@media (min-width: 640px) {
    .rating-star {
        width: 3.5rem;
        height: 3.5rem;
    }
}

/* Emoji Rating Styles */
.survey-emoji-rating-option {
    @apply flex flex-col items-center justify-center;
    @apply cursor-pointer transition-all duration-200;
    opacity: 0.5;
}

.survey-emoji-rating-input {
    @apply sr-only;
}

.survey-emoji-rating-input:checked~.survey-emoji-rating-label,
.survey-emoji-rating-option:has(.survey-emoji-rating-input:checked) {
    opacity: 1;
}

.survey-emoji-rating-label {
    @apply flex flex-col items-center justify-center;
    @apply text-gray-700 font-semibold;
    @apply transition-all duration-200;
}

.survey-emoji-rating-option:hover {
    transform: scale(1.05);
    opacity: 1;
}

/* Submit Button */
.survey-submit-btn {
    @apply px-8 py-3 bg-gradient-to-r from-slate-700 to-slate-800;
    @apply text-white font-semibold rounded-lg;
    @apply shadow-lg;
    @apply transition-all duration-200;
    @apply hover:from-slate-800 hover:to-slate-900;
    @apply hover:shadow-xl;
    @apply hover:scale-105;
    @apply flex items-center justify-center;
    min-width: 180px;
    box-shadow: 0 10px 15px -3px rgba(51, 65, 85, 0.3), 0 4px 6px -2px rgba(51, 65, 85, 0.3);
}

.survey-submit-btn:hover {
    box-shadow: 0 20px 25px -5px rgba(51, 65, 85, 0.4), 0 10px 10px -5px rgba(51, 65, 85, 0.4);
}

.survey-submit-btn:active {
    transform: scale(1);
}

.survey-submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.survey-submit-btn:disabled:hover {
    transform: scale(1);
    box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.3);
}

/* Fade transitions */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.bg-1 {
    background-color: #e4ded1;
}

.bg-2 {
    background-color: #cdb58e;
}

.bg-3 {
    background-color: #AC8440;
}

.ht-300 {
    height: 300px;
}

.box-shadow-1 {
    box-shadow: 0px -2px 25px rgba(0, 0, 0, 0.3);
}

.box-shadow-2 {
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12);
}

.bg-btn {
    background-color: #b47c2c;
}

/* Progress Bar Styles */
.progress-bar-container {
    height: 18px;
    background-color: #cdb58e;
    position: relative;
    overflow: visible;
}

.progress-bar-fill {
    height: 100%;
    background-color: #b47c2c;
    transition: width 0.3s ease;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 0px;
}

.progress-bar-text {
    color: #000;
    background-color: #fff;
    padding: 0px 3px;
    font-size: 12px;
    white-space: nowrap;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12);
}

.text-xxxs {
    font-size: 8px;
}

.text-color-1 {
    color: #AC8440;
}

@responsive {
    .ht-55 {
        height: 55px;
    }

    .ht-auto {
        height: auto;
    }
}

.survey-page-indicator {
    margin-bottom: 1.5rem;
    text-align: center;
}

.survey-page-indicator-label {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.875rem;
    border-radius: 9999px;
    background-color: #f5f3ff;
    color: #5b21b6;
    font-size: 0.8125rem;
    font-weight: 600;
}

.survey-page-title-wrap {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.survey-page-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
}

.survey-nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-width: 8rem;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s ease, background-color 0.15s ease;
}

.survey-nav-btn-prev {
    background-color: #ffffff;
    color: #374151;
    border-top: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
}

.survey-nav-btn-next,
.survey-nav-btn-submit {
    background-color: #b47c2c;
    color: #ffffff;
}

.survey-nav-btn-disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
</style>
